// StudentProfileApp.jsx - Premium Blue & White Redesign
import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import RegularAttendanceView from "./RegularAttendanceView";
import Marksheet from "./Marksheet";

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700;800&family=Inter:wght@300;400;500;600;700&display=swap');

.sp-root *{box-sizing:border-box;margin:0;padding:0}
.sp-root{font-family:'Inter',sans-serif;background:#E3F2FD;min-height:100vh;color:#0D2436;position:relative;overflow-x:hidden}

/* ── Premium Background Ornaments ── */
.sp-bg-ornament{position:fixed;border-radius:50%;opacity:0.04;pointer-events:none;z-index:0}
.sp-bg-ornament-1{top:-200px;right:-200px;width:500px;height:500px;background:radial-gradient(circle,#0D47A1,transparent 70%)}
.sp-bg-ornament-2{bottom:-200px;left:-200px;width:400px;height:400px;background:radial-gradient(circle,#2196F3,transparent 70%)}
.sp-bg-ornament-3{top:50%;left:50%;transform:translate(-50%,-50%);width:600px;height:600px;background:radial-gradient(circle,#90CAF9,transparent 70%)}

/* ── Premium Header ── */
.sp-header{
  background:linear-gradient(135deg,#0A1628 0%,#0D47A1 50%,#2196F3 100%);
  padding:14px 28px;
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:16px;
  box-shadow:0 4px 30px rgba(13,71,161,0.25);
  border-bottom:3px solid #90CAF9;
  flex-wrap:nowrap;
  min-height:64px;
  position:relative;
  z-index:10;
}
.sp-logo{font-family:'Playfair Display',serif;font-weight:700;font-size:20px;color:#fff;letter-spacing:0.5px;white-space:nowrap}
.sp-logo span{color:#90CAF9}
.sp-sub{font-size:11px;color:rgba(255,255,255,0.6);margin-top:2px;font-weight:400;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.sp-header-brand{min-width:0;flex:1}

.sp-header-actions{display:flex;align-items:center;gap:12px;flex-shrink:0}
.sp-search{display:flex;align-items:center;gap:8px;background:rgba(255,255,255,0.08);border:1px solid rgba(255,255,255,0.12);border-radius:40px;padding:8px 16px;width:220px;transition:all .2s}
.sp-search:focus-within{background:rgba(255,255,255,0.15);border-color:rgba(144,202,249,0.5);box-shadow:0 0 0 3px rgba(144,202,249,0.1)}
.sp-search input{background:none;border:none;outline:none;color:#fff;font-size:13px;font-family:'Inter',sans-serif;width:100%}
.sp-search input::placeholder{color:rgba(255,255,255,0.4)}

/* ── Hamburger ── */
.sp-hamburger{
  display:none;
  background:rgba(255,255,255,0.1);
  border:1px solid rgba(255,255,255,0.15);
  color:#fff;
  width:40px;height:40px;
  border-radius:10px;
  cursor:pointer;
  align-items:center;justify-content:center;
  font-size:20px;
  flex-shrink:0;
  transition:background .15s;
}
.sp-hamburger:hover{background:rgba(255,255,255,0.2)}

/* ── Body Layout ── */
.sp-body{display:grid;grid-template-columns:280px 1fr;min-height:calc(100vh - 64px);position:relative;z-index:5}

/* ── Sidebar ── */
.sp-sidebar{
  background:#FFFFFF;
  border-right:1px solid #90CAF9;
  display:flex;
  flex-direction:column;
  overflow:hidden;
  box-shadow:4px 0 20px rgba(13,71,161,0.04);
}
.sidebar-tree{flex:1;overflow-y:auto;padding:8px 0}
.sidebar-tree::-webkit-scrollbar{width:4px}
.sidebar-tree::-webkit-scrollbar-track{background:#E3F2FD}
.sidebar-tree::-webkit-scrollbar-thumb{background:#90CAF9;border-radius:4px}

.tree-class{border-bottom:1px solid #E3F2FD}
.tree-class-row{
  display:flex;
  align-items:center;
  gap:12px;
  padding:12px 20px;
  cursor:pointer;
  transition:all .15s;
  user-select:none;
  border-left:3px solid transparent;
}
.tree-class-row:hover{background:#E3F2FD}
.tree-class-row.active{background:#E3F2FD;border-left-color:#0D47A1}
.tree-emoji{font-size:16px;width:24px;text-align:center;flex-shrink:0}
.tree-label{font-size:13px;font-weight:600;color:#0D2436;flex:1}
.tree-count{font-size:10px;color:#2196F3;background:#E3F2FD;border-radius:20px;padding:2px 10px;font-weight:600}
.tree-arr{color:#90CAF9;font-size:14px;transition:transform .2s;flex-shrink:0}
.tree-arr.open{transform:rotate(90deg)}

.tree-section{border-left:3px solid #90CAF9;margin-left:36px}
.tree-sec-row{display:flex;align-items:center;gap:8px;padding:0;cursor:pointer}
.tree-sec-row:hover .tree-sec-inner{background:#E3F2FD}
.tree-sec-row.active .tree-sec-inner{background:#E3F2FD;border-left-color:#0D47A1}
.tree-sec-inner{flex:1;display:flex;align-items:center;justify-content:space-between;padding:8px 16px;border-left:3px solid transparent}
.tree-slabel{font-size:12px;color:#2196F3;flex:1;font-weight:500}

.tree-tt-row{
  display:flex;
  align-items:center;
  gap:10px;
  padding:8px 16px 8px 24px;
  cursor:pointer;
  transition:all .15s;
  border-left:3px solid transparent;
}
.tree-tt-row:hover{background:#E3F2FD}
.tree-tt-row.active{background:#E3F2FD;border-left-color:#0D47A1}
.tree-tt-label{font-size:12px;color:#0D2436;font-weight:500}

.tree-approval-row{
  display:flex;
  align-items:center;
  gap:10px;
  padding:8px 16px 8px 24px;
  cursor:pointer;
  transition:all .15s;
  border-left:3px solid transparent;
}
.tree-approval-row:hover{background:#E3F2FD}
.tree-approval-row.active{background:#E3F2FD;border-left-color:#0D47A1}
.tree-approval-label{font-size:12px;color:#2196F3;font-weight:500}

.tree-profile-row{
  display:flex;
  align-items:center;
  gap:12px;
  padding:12px 20px;
  cursor:pointer;
  transition:all .15s;
  border-bottom:1px solid #E3F2FD;
  border-left:3px solid transparent;
}
.tree-profile-row:hover{background:#E3F2FD}
.tree-profile-row.active{background:#E3F2FD;border-left-color:#0D47A1}

.tree-circulars-row{
  display:flex;
  align-items:center;
  gap:12px;
  padding:12px 20px;
  cursor:pointer;
  transition:all .15s;
  border-bottom:1px solid #E3F2FD;
  border-left:3px solid transparent;
}
.tree-circulars-row:hover{background:#E3F2FD}
.tree-circulars-row.active{background:#E3F2FD;border-left-color:#0D47A1}

.tree-dailyatt-row{
  display:flex;
  align-items:center;
  gap:12px;
  padding:12px 20px;
  cursor:pointer;
  transition:all .15s;
  border-bottom:1px solid #E3F2FD;
  border-left:3px solid transparent;
}
.tree-dailyatt-row:hover{background:#E3F2FD}
.tree-dailyatt-row.active{background:#E3F2FD;border-left-color:#0D47A1}
.tree-dailyatt-label{font-size:13px;font-weight:600;color:#2196F3;flex:1}

.tree-badge{
  background:#0D47A1;
  color:#FFFFFF;
  border-radius:20px;
  font-size:10px;
  font-weight:700;
  padding:2px 10px;
  min-width:20px;
  text-align:center;
}

/* ── Main Panel ── */
.sp-main{display:flex;flex-direction:column;overflow:hidden;min-width:0;background:#E3F2FD}
.sp-main-body{flex:1;padding:24px 28px;overflow-y:auto}

/* ── Welcome ── */
.welcome-box{
  display:flex;
  align-items:center;
  justify-content:center;
  height:400px;
  flex-direction:column;
  gap:16px;
  background:#FFFFFF;
  border-radius:20px;
  border:1px solid #90CAF9;
  box-shadow:0 4px 20px rgba(13,71,161,0.04);
}
.welcome-icon{font-size:56px;color:#2196F3}
.welcome-title{font-family:'Playfair Display',serif;font-size:22px;font-weight:700;color:#0D2436}
.welcome-sub{font-size:14px;text-align:center;max-width:400px;color:#4A6D8C}

/* ── Stats Grid ── */
.stats-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:16px;margin-bottom:24px}
.stat-card{
  background:#FFFFFF;
  border-radius:16px;
  padding:20px 24px;
  border:1px solid #90CAF9;
  box-shadow:0 4px 20px rgba(13,71,161,0.04);
  transition:all .2s;
}
.stat-card:hover{transform:translateY(-2px);box-shadow:0 8px 30px rgba(13,71,161,0.08)}
.stat-card.total{border-left:4px solid #0D47A1}
.stat-card.present{border-left:4px solid #2196F3}
.stat-card.absent{border-left:4px solid #90CAF9}
.stat-card.late{border-left:4px solid #E3F2FD}
.stat-value{font-family:'Playfair Display',serif;font-size:32px;font-weight:700;color:#0D2436}
.stat-label{font-size:12px;color:#4A6D8C;margin-top:4px;font-weight:500;text-transform:uppercase;letter-spacing:0.5px}

/* ── Student Table ── */
.student-table-container{
  overflow-x:auto;
  border:1px solid #90CAF9;
  border-radius:16px;
  background:#fff;
  box-shadow:0 4px 20px rgba(13,71,161,0.04);
}
.student-table{width:100%;border-collapse:collapse}
.student-table th{
  padding:14px 18px;
  text-align:left;
  font-size:10px;
  font-weight:700;
  color:#0D47A1;
  text-transform:uppercase;
  letter-spacing:0.5px;
  background:#E3F2FD;
  border-bottom:2px solid #90CAF9;
}
.student-table td{padding:12px 18px;font-size:13px;color:#0D2436;border-bottom:1px solid #E3F2FD}
.student-table tr:last-child td{border-bottom:none}
.student-table tr:hover{background:#E3F2FD;cursor:pointer}

/* ── Timetable ── */
.tt-wrap{
  background:#FFFFFF;
  border-radius:16px;
  border:1px solid #90CAF9;
  overflow:hidden;
  box-shadow:0 4px 20px rgba(13,71,161,0.04);
}
.tt-header{
  background:linear-gradient(135deg,#0D47A1,#2196F3);
  padding:18px 24px;
  display:flex;
  align-items:center;
  justify-content:space-between;
}
.tt-title{font-family:'Playfair Display',serif;font-size:16px;font-weight:700;color:#fff}
.tt-subtitle{font-size:11px;color:#90CAF9;margin-top:3px}
.tt-empty{padding:60px;text-align:center;color:#2196F3;font-size:14px}
.tt-grid-wrap{overflow-x:auto;padding:0}
.tt-table{width:100%;border-collapse:collapse;min-width:700px}
.tt-table th{
  padding:12px 16px;
  font-size:10px;
  font-weight:700;
  color:#0D47A1;
  text-transform:uppercase;
  letter-spacing:0.5px;
  background:#E3F2FD;
  border-bottom:2px solid #90CAF9;
  text-align:center;
}
.tt-table th:first-child{text-align:left;width:100px;border-right:2px solid #90CAF9}
.tt-table td{
  padding:10px 14px;
  font-size:13px;
  border-bottom:1px solid #E3F2FD;
  vertical-align:top;
  text-align:center;
}
.tt-table td:first-child{font-weight:600;color:#0D47A1;font-size:12px;background:#E3F2FD;border-right:2px solid #90CAF9;white-space:nowrap;text-align:left}
.tt-table tr:last-child td{border-bottom:none}
.tt-cell{display:flex;flex-direction:column;gap:2px;align-items:center}
.tt-subj{font-weight:600;color:#0D2436;font-size:12px}
.tt-teach{font-size:10px;color:#2196F3}
.tt-empty-cell{color:#90CAF9;font-size:11px}
.tt-break-cell{background:#E3F2FD;color:#0D47A1;font-size:11px;font-weight:600;text-align:center;letter-spacing:0.5px;padding:4px 8px;border-radius:4px}

/* ── Approval Panel ── */
.ap-wrap{
  background:#FFFFFF;
  border-radius:16px;
  border:1px solid #90CAF9;
  overflow:hidden;
  box-shadow:0 4px 20px rgba(13,71,161,0.04);
}
.ap-header{
  background:linear-gradient(135deg,#0D47A1,#2196F3);
  padding:18px 24px;
}
.ap-title{font-family:'Playfair Display',serif;font-size:16px;font-weight:700;color:#fff}
.ap-subtitle{font-size:11px;color:#90CAF9;margin-top:3px}
.ap-empty{padding:60px;text-align:center;color:#2196F3;font-size:14px}
.ap-card{
  background:#fff;
  border:1px solid #90CAF9;
  border-radius:12px;
  padding:16px 20px;
  margin-bottom:12px;
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:12px;
  transition:all .15s;
}
.ap-card:hover{box-shadow:0 4px 16px rgba(13,71,161,0.06)}
.ap-info{flex:1;min-width:0}
.ap-name{font-size:14px;font-weight:700;color:#0D2436}
.ap-meta{font-size:11px;color:#2196F3;margin-top:3px}
.ap-actions{display:flex;gap:8px;flex-shrink:0}
.ap-btn-approve{
  background:#E3F2FD;
  color:#0D47A1;
  border:1px solid #90CAF9;
  padding:7px 18px;
  border-radius:40px;
  font-size:12px;
  font-weight:600;
  cursor:pointer;
  font-family:'Inter',sans-serif;
  transition:all .15s;
}
.ap-btn-approve:hover{background:#0D47A1;color:#fff}
.ap-btn-reject{
  background:#fff;
  color:#4A6D8C;
  border:1px solid #E3F2FD;
  padding:7px 18px;
  border-radius:40px;
  font-size:12px;
  font-weight:600;
  cursor:pointer;
  font-family:'Inter',sans-serif;
  transition:all .15s;
}
.ap-btn-reject:hover{background:#E3F2FD;border-color:#90CAF9}

/* ── Approval Status ── */
.as-wrap{
  background:#FFFFFF;
  border-radius:16px;
  border:1px solid #90CAF9;
  overflow:hidden;
  box-shadow:0 4px 20px rgba(13,71,161,0.04);
}
.as-header{
  background:linear-gradient(135deg,#0D47A1,#2196F3);
  padding:18px 24px;
  display:flex;
  align-items:center;
  justify-content:space-between;
}
.as-title{font-family:'Playfair Display',serif;font-size:16px;font-weight:700;color:#fff}
.as-subtitle{font-size:11px;color:#90CAF9;margin-top:3px}
.as-stats{display:flex;gap:8px;flex-wrap:wrap}
.as-stat{
  background:rgba(255,255,255,0.12);
  border-radius:40px;
  padding:4px 14px;
  font-size:12px;
  font-weight:600;
  color:#fff;
}
.as-empty{padding:60px;text-align:center;color:#2196F3;font-size:14px}
.as-table-wrap{overflow-x:auto}
.as-table{width:100%;border-collapse:collapse}
.as-table th{
  padding:12px 16px;
  text-align:left;
  font-size:10px;
  font-weight:700;
  color:#0D47A1;
  text-transform:uppercase;
  letter-spacing:0.5px;
  background:#E3F2FD;
  border-bottom:2px solid #90CAF9;
}
.as-table td{padding:13px 16px;font-size:13px;color:#0D2436;border-bottom:1px solid #E3F2FD;vertical-align:middle}
.as-table tr:last-child td{border-bottom:none}
.as-table tr:hover{background:#E3F2FD}
.as-badge{
  display:inline-flex;
  align-items:center;
  gap:6px;
  padding:4px 14px;
  border-radius:40px;
  font-size:11px;
  font-weight:700;
}
.as-badge.approved{background:#E3F2FD;color:#0D47A1}
.as-badge.rejected{background:#E3F2FD;color:#4A6D8C}
.as-badge.pending{background:#90CAF9;color:#0D47A1}
.as-filter-bar{
  display:flex;
  gap:8px;
  padding:14px 20px;
  border-bottom:1px solid #E3F2FD;
  background:#E3F2FD;
  flex-wrap:wrap;
  align-items:center;
}
.as-filter-btn{
  padding:5px 16px;
  border-radius:40px;
  font-size:12px;
  font-weight:600;
  border:1px solid #90CAF9;
  cursor:pointer;
  font-family:'Inter',sans-serif;
  transition:all .15s;
}
.as-filter-btn.active{background:#0D47A1;color:#fff;border-color:#0D47A1}
.as-filter-btn:not(.active){background:#fff;color:#2196F3}
.as-filter-btn:not(.active):hover{background:#E3F2FD;border-color:#90CAF9}
.as-search{
  display:flex;
  align-items:center;
  gap:6px;
  background:#fff;
  border:1px solid #90CAF9;
  border-radius:40px;
  padding:5px 14px;
  margin-left:auto;
}
.as-search input{border:none;outline:none;font-size:12px;font-family:'Inter',sans-serif;color:#0D2436;width:160px}

/* ── Profile ── */
.prof-wrap{
  background:#FFFFFF;
  border-radius:20px;
  border:1px solid #90CAF9;
  overflow:hidden;
  box-shadow:0 4px 20px rgba(13,71,161,0.04);
}
.prof-banner{
  height:120px;
  background:linear-gradient(135deg,#0D47A1 0%,#0D47A1 50%,#2196F3 100%);
  position:relative;
}
.prof-avatar{
  width:84px;height:84px;
  border-radius:20px;
  position:absolute;bottom:-36px;left:28px;
  border:4px solid #fff;
  display:flex;align-items:center;justify-content:center;
  font-family:'Playfair Display',serif;
  font-weight:800;font-size:28px;
  box-shadow:0 4px 20px rgba(13,71,161,0.15);
}
.prof-body{padding:52px 28px 28px}
.prof-name{font-family:'Playfair Display',serif;font-size:22px;font-weight:800;color:#0D2436;margin-bottom:4px}
.prof-role{font-size:13px;color:#2196F3;margin-bottom:16px}
.prof-badges{display:flex;gap:8px;flex-wrap:wrap;margin-bottom:24px}
.prof-badge{padding:4px 14px;border-radius:40px;font-size:11px;font-weight:600}
.prof-grid{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:24px}
.prof-field{background:#E3F2FD;border-radius:12px;padding:14px 16px;border:1px solid #90CAF9}
.prof-field-label{font-size:10px;color:#2196F3;text-transform:uppercase;letter-spacing:0.6px;font-weight:600;margin-bottom:5px}
.prof-field-val{font-size:14px;color:#0D2436;font-weight:600}
.prof-cred-box{background:#E3F2FD;border:1px solid #90CAF9;border-radius:14px;padding:18px 20px}
.prof-cred-title{font-size:12px;font-weight:700;color:#2196F3;margin-bottom:14px;display:flex;align-items:center;gap:6px}
.prof-cred-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px}
.prof-cred-card{background:#fff;border-radius:10px;padding:12px 14px;border:1px solid #90CAF9}
.prof-cred-key{font-size:10px;color:#2196F3;text-transform:uppercase;letter-spacing:0.5px;margin-bottom:4px}
.prof-cred-val{font-size:13px;font-weight:700;color:#0D2436;display:flex;align-items:center;gap:8px}

/* ── Student Details ── */
.sd-wrap{
  background:#FFFFFF;
  border-radius:20px;
  border:1px solid #90CAF9;
  overflow:hidden;
  box-shadow:0 4px 20px rgba(13,71,161,0.04);
}
.sd-header{
  background:linear-gradient(135deg,#0D47A1,#2196F3);
  padding:20px 26px;
  display:flex;
  align-items:center;
  justify-content:space-between;
  flex-wrap:wrap;
  gap:12px;
}
.sd-title{font-family:'Playfair Display',serif;font-size:17px;font-weight:800;color:#fff}
.sd-subtitle{font-size:11px;color:#90CAF9;margin-top:3px}
.sd-toolbar{
  display:flex;
  align-items:center;
  gap:10px;
  padding:14px 20px;
  border-bottom:1px solid #90CAF9;
  background:#E3F2FD;
  flex-wrap:wrap;
}
.sd-search-box{
  display:flex;
  align-items:center;
  gap:6px;
  background:#fff;
  border:1px solid #90CAF9;
  border-radius:40px;
  padding:7px 14px;
  flex:1;
  min-width:160px;
}
.sd-search-box input{border:none;outline:none;font-size:13px;font-family:'Inter',sans-serif;color:#0D2436;width:100%}
.sd-roll-btn{
  background:linear-gradient(90deg,#0D47A1,#2196F3);
  color:#fff;
  border:none;
  padding:9px 18px;
  border-radius:40px;
  font-size:12px;
  font-weight:700;
  cursor:pointer;
  font-family:'Inter',sans-serif;
  transition:all .15s;
  white-space:nowrap;
}
.sd-roll-btn:hover{background:linear-gradient(90deg,#0D47A1,#0D47A1);box-shadow:0 4px 14px rgba(13,71,161,0.3)}
.sd-roll-btn:disabled{opacity:0.6;cursor:wait}
.sd-empty{padding:60px;text-align:center;color:#2196F3}
.sd-table-wrap{overflow-x:auto}
.sd-table{width:100%;border-collapse:collapse}
.sd-table thead tr{background:#E3F2FD}
.sd-table th{
  padding:11px 14px;
  text-align:left;
  font-size:10px;
  font-weight:700;
  color:#0D47A1;
  text-transform:uppercase;
  letter-spacing:0.5px;
  border-bottom:2px solid #90CAF9;
  white-space:nowrap;
}
.sd-table td{padding:10px 14px;font-size:13px;color:#0D2436;border-bottom:1px solid #E3F2FD;vertical-align:middle}
.sd-table tr:last-child td{border-bottom:none}
.sd-table tr:hover{background:#E3F2FD}

/* ── Edit Modal ── */
.esm-overlay{
  position:fixed;inset:0;
  background:rgba(13,71,161,0.5);
  z-index:2000;
  display:flex;align-items:center;justify-content:center;
  padding:16px;
  backdrop-filter:blur(4px);
}
.esm-modal{
  background:#fff;
  border-radius:20px;
  width:100%;max-width:740px;max-height:92vh;
  display:flex;flex-direction:column;
  overflow:hidden;
  box-shadow:0 28px 72px rgba(13,71,161,0.2);
}
.esm-header{
  background:linear-gradient(135deg,#0D47A1,#2196F3);
  padding:18px 22px;
  display:flex;align-items:center;justify-content:space-between;
  flex-shrink:0;
}
.esm-title{font-family:'Playfair Display',serif;font-size:16px;font-weight:800;color:#fff}
.esm-close{
  background:rgba(255,255,255,0.15);
  border:1px solid rgba(255,255,255,0.2);
  color:#fff;
  width:34px;height:34px;
  border-radius:10px;
  cursor:pointer;font-size:18px;
  display:flex;align-items:center;justify-content:center;
}
.esm-close:hover{background:rgba(255,255,255,0.25)}
.esm-body{overflow-y:auto;padding:20px 22px;flex:1}
.esm-footer{
  padding:14px 22px;
  border-top:1px solid #90CAF9;
  display:flex;gap:10px;justify-content:flex-end;
  flex-shrink:0;background:#E3F2FD;
  flex-wrap:wrap;
}
.esm-section{margin-bottom:22px}
.esm-section-title{
  font-size:11px;font-weight:700;color:#2196F3;
  text-transform:uppercase;letter-spacing:0.7px;
  margin-bottom:10px;padding-bottom:6px;
  border-bottom:1px solid #90CAF9;
}
.esm-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px}
.esm-field{display:flex;flex-direction:column;gap:5px}
.esm-label{font-size:11px;font-weight:600;color:#2196F3;text-transform:uppercase;letter-spacing:0.5px}
.esm-input{
  padding:9px 12px;
  border:1.5px solid #90CAF9;
  border-radius:10px;
  font-size:13px;font-family:'Inter',sans-serif;
  color:#0D2436;background:#fff;
  outline:none;transition:all .15s;
}
.esm-input:focus{border-color:#0D47A1;box-shadow:0 0 0 3px rgba(13,71,161,0.08)}
.esm-input-readonly{background:#E3F2FD;color:#4A6D8C;cursor:not-allowed}
.esm-select{
  padding:9px 12px;
  border:1.5px solid #90CAF9;
  border-radius:10px;
  font-size:13px;font-family:'Inter',sans-serif;
  color:#0D2436;background:#fff;
  outline:none;transition:all .15s;cursor:pointer;
}
.esm-select:focus{border-color:#0D47A1;box-shadow:0 0 0 3px rgba(13,71,161,0.08)}
.esm-photo-area{
  display:flex;flex-direction:column;align-items:center;gap:12px;
  padding:20px;border:2px dashed #90CAF9;
  border-radius:14px;background:#E3F2FD;
  cursor:pointer;transition:all .2s;
}
.esm-photo-area:hover{border-color:#0D47A1;background:#E3F2FD}
.esm-photo-preview{width:90px;height:90px;border-radius:16px;object-fit:cover;border:3px solid #0D47A1}
.esm-photo-hint{font-size:12px;color:#0D2436;font-weight:600;text-align:center}
.esm-photo-sub{font-size:11px;color:#2196F3;text-align:center}
.esm-save-btn{
  background:linear-gradient(90deg,#0D47A1,#2196F3);
  color:#fff;border:none;
  padding:10px 24px;border-radius:40px;
  font-size:13px;font-weight:700;
  cursor:pointer;font-family:'Inter',sans-serif;
  transition:all .15s;
}
.esm-save-btn:hover:not(:disabled){box-shadow:0 4px 14px rgba(13,71,161,0.35)}
.esm-save-btn:disabled{opacity:0.6;cursor:wait}
.esm-cancel-btn{
  background:#fff;color:#2196F3;
  border:1.5px solid #90CAF9;
  padding:10px 20px;border-radius:40px;
  font-size:13px;font-weight:600;
  cursor:pointer;font-family:'Inter',sans-serif;
  transition:all .15s;
}
.esm-cancel-btn:hover{background:#E3F2FD}

/* ── Drawer ── */
.sp-drawer-overlay{
  display:none;
  position:fixed;inset:0;
  background:rgba(13,71,161,0.4);
  z-index:900;
  backdrop-filter:blur(2px)
}
.sp-drawer-overlay.open{display:block}

.sp-drawer{
  position:fixed;
  top:0;left:0;bottom:0;
  width:300px;
  background:#fff;
  z-index:1000;
  transform:translateX(-100%);
  transition:transform .28s cubic-bezier(.4,0,.2,1);
  overflow:hidden;
  display:flex;
  flex-direction:column;
  box-shadow:4px 0 32px rgba(13,71,161,0.12);
}
.sp-drawer.open{transform:translateX(0)}
.sp-drawer-header{
  background:linear-gradient(135deg,#0D47A1 0%,#0D47A1 50%,#2196F3 100%);
  padding:14px 16px;
  display:flex;
  align-items:center;
  justify-content:space-between;
  flex-shrink:0;
}
.sp-drawer-title{font-family:'Playfair Display',serif;font-weight:700;font-size:15px;color:#fff}
.sp-drawer-title span{color:#90CAF9}
.sp-drawer-close{
  background:rgba(255,255,255,0.15);
  border:1px solid rgba(255,255,255,0.2);
  color:#fff;width:32px;height:32px;
  border-radius:8px;cursor:pointer;
  font-size:16px;
  display:flex;align-items:center;justify-content:center;
}
.sp-drawer-close:hover{background:rgba(255,255,255,0.25)}
.sp-drawer-tree{flex:1;overflow-y:auto;padding:8px 0}

/* ── Mobile Responsive ── */
@media (max-width:768px){
  .sp-header{padding:10px 16px;flex-wrap:wrap;gap:8px;min-height:56px}
  .sp-logo{font-size:15px}
  .sp-sub{display:none}
  .sp-search{width:120px;padding:6px 12px}
  .sp-search input{font-size:12px}
  .sp-hamburger{display:flex}
  .sp-body{grid-template-columns:1fr;min-height:calc(100vh - 56px)}
  .sp-sidebar{display:none}
  .sp-main-body{padding:12px}
  .stats-grid{grid-template-columns:repeat(2,1fr);gap:10px;margin-bottom:16px}
  .stat-card{padding:14px}
  .stat-value{font-size:24px}
  .welcome-box{height:auto;padding:40px 20px;min-height:280px}
  .welcome-title{font-size:18px;text-align:center}
  .welcome-sub{font-size:12px}
  .prof-grid{grid-template-columns:1fr}
  .prof-cred-grid{grid-template-columns:1fr}
  .prof-body{padding:48px 16px 20px}
  .prof-name{font-size:18px}
  .prof-banner{height:100px}
  .prof-avatar{width:72px;height:72px;font-size:24px;bottom:-28px;left:16px}
  .esm-grid{grid-template-columns:1fr}
  .esm-overlay{padding:12px}
  .esm-modal{border-radius:14px}
  .esm-body{padding:16px}
  .esm-footer{padding:12px 16px}
  .sd-header{flex-direction:column;align-items:flex-start;padding:16px}
  .sd-roll-btn{font-size:11px;padding:8px 14px}
  .sd-toolbar{padding:10px 12px;gap:8px}
  .as-header{flex-direction:column;gap:10px;padding:16px}
  .as-filter-bar{padding:10px 12px;gap:6px}
  .as-search{margin-left:0;width:100%}
  .as-search input{width:100%}
  .ap-card{flex-direction:column;align-items:flex-start;gap:10px}
  .ap-actions{width:100%;justify-content:flex-end}
  .tt-header{flex-direction:column;align-items:flex-start;gap:8px;padding:14px 16px}
  .att-action-row{flex-direction:column;align-items:flex-start !important;gap:10px !important}
  .att-action-row > div{width:100%}
  .att-btn-group{flex-wrap:wrap}
  .student-table th,.student-table td{padding:8px 10px;font-size:11px}
}

@media (max-width:400px){
  .sp-logo{font-size:13px}
  .sp-search{display:none}
  .stats-grid{grid-template-columns:1fr 1fr}
  .student-table th,.student-table td{padding:6px 8px;font-size:10px}
  .stat-card{padding:10px}
  .stat-value{font-size:20px}
}
`;

const DAYS = ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];

const DEPT_COLOR = {
  Maths:              { bg:"#E3F2FD", color:"#0D47A1" },
  Science:            { bg:"#E3F2FD", color:"#2196F3" },
  Tamil:              { bg:"#E3F2FD", color:"#0D47A1" },
  English:            { bg:"#E3F2FD", color:"#0D47A1" },
  Social:             { bg:"#E3F2FD", color:"#2196F3" },
  Admin:              { bg:"#E3F2FD", color:"#0D47A1" },
  "Bio Maths":        { bg:"#E3F2FD", color:"#0D47A1" },
  "Computer Science": { bg:"#E3F2FD", color:"#2196F3" },
  "Art & Science":    { bg:"#E3F2FD", color:"#0D47A1" },
  Commerce:           { bg:"#E3F2FD", color:"#2196F3" },
};

const AVATAR_PALETTE = [
  { bg:"#E3F2FD", color:"#0D47A1" },
  { bg:"#E3F2FD", color:"#2196F3" },
  { bg:"#E3F2FD", color:"#0D47A1" },
  { bg:"#E3F2FD", color:"#2196F3" },
  { bg:"#E3F2FD", color:"#0D47A1" },
  { bg:"#E3F2FD", color:"#2196F3" },
];
function avatarColor(i) { return AVATAR_PALETTE[i % AVATAR_PALETTE.length]; }

function generateRollPrefix(className) {
  if (!className) return "C";
  const str     = className.trim();
  const dashIdx = str.lastIndexOf("-");
  const section = dashIdx >= 0 ? str.slice(dashIdx + 1).trim().toUpperCase() : "A";
  const before  = dashIdx >= 0 ? str.slice(0, dashIdx).trim() : str;
  const numMatch = before.match(/\d+/);
  const standard = numMatch ? numMatch[0] : "";
  if (standard === "11" || standard === "12") {
    const lower = before.toLowerCase();
    let groupCode = "X";
    if      (lower.includes("bio"))      groupCode = "B";
    else if (lower.includes("computer")) groupCode = "P";
    else if (lower.includes("commerce")) groupCode = "C";
    else if (lower.includes("art"))      groupCode = "A";
    return `C${groupCode}${section}`;
  }
  return `C${standard}${section}`;
}

function formatRollNumber(prefix, seq) {
  return `${prefix}${String(seq).padStart(3, "0")}`;
}

// ─── Bell Icon ────────────────────────────────────────────────────────────────
function BellIcon({ unreadCount, onClick }) {
  const has = unreadCount > 0;
  return (
    <div 
      onClick={onClick} 
      title={has ? `${unreadCount} unread messages` : "No new messages"}
      style={{ 
        cursor:"pointer", 
        display:"flex", 
        alignItems:"center", 
        gap:6,
        position:"relative"
      }}
    >
      <div style={{
        padding:7, 
        borderRadius:8,
        background: has ? "rgba(255,255,255,0.15)" : "transparent",
        border: has ? "1px solid rgba(255,255,255,0.25)" : "1px solid rgba(255,255,255,0.06)"
      }}>
        <span style={{ fontSize:17, color: has ? "#fff" : "rgba(255,255,255,0.5)" }}>{has ? "🔔" : "🔕"}</span>
      </div>
      {has && (
        <div style={{ 
          position:"absolute",
          top:-4,
          right:-4,
          minWidth:20, 
          height:20, 
          borderRadius:20, 
          background:"#2196F3", 
          color:"#fff",
          fontSize:11, 
          fontWeight:700, 
          display:"flex", 
          alignItems:"center", 
          justifyContent:"center",
          padding:"0 6px",
          border:"2px solid #0D47A1",
          boxShadow:"0 2px 8px rgba(33,150,243,0.3)"
        }}>
          {unreadCount}
        </div>
      )}
    </div>
  );
}

// ─── Staff Profile Panel ──────────────────────────────────────────────────────
function StaffProfilePanel({ staffData, advisorClasses }) {
  const [showPwd, setShowPwd] = useState(false);
  if (!staffData) return (
    <div className="prof-wrap">
      <div className="prof-banner" />
      <div className="prof-body"><div style={{ textAlign:"center", color:"#2196F3", padding:"40px 0" }}>Loading profile...</div></div>
    </div>
  );
  const dc = DEPT_COLOR[staffData.dept] || { bg:"#E3F2FD", color:"#0D47A1" };
  const initials = staffData.initials ||
    staffData.name?.trim().split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase() || "??";
  const fields = [
    ["Department",    staffData.dept],
    ["Experience",    staffData.exp ? `${staffData.exp} years` : "—"],
    ["Phone",         staffData.phone || "—"],
    ["Email",         staffData.email || "—"],
    ["Qualification", staffData.qual || "—"],
    ["Year Joined",   staffData.join || "—"],
  ];
  return (
    <div className="prof-wrap">
      <div className="prof-banner">
        <div className="prof-avatar" style={{ background:dc.bg, color:dc.color }}>{initials}</div>
      </div>
      <div className="prof-body">
        <div className="prof-name">{staffData.name}</div>
        <div className="prof-role">{staffData.role}</div>
        <div className="prof-badges">
          <span className="prof-badge" style={{ background:dc.bg, color:dc.color }}>{staffData.dept}</span>
          <span className="prof-badge" style={{ background:"#E3F2FD", color:"#2196F3" }}>{staffData.exp} yrs exp</span>
          <span className="prof-badge" style={{
            background: staffData.status === "present" ? "#E3F2FD" : "#E3F2FD",
            color:      staffData.status === "present" ? "#0D47A1" : "#4A6D8C"
          }}>{staffData.status === "present" ? "Present ✓" : "On Leave"}</span>
        </div>
        {staffData.subjects?.length > 0 && (
          <div style={{ background:"#E3F2FD", border:"1px solid #90CAF9", borderRadius:14, padding:"18px 20px", marginBottom:24 }}>
            <div style={{ fontSize:12, fontWeight:700, color:"#0D47A1", marginBottom:12 }}>📚 Subjects Taught</div>
            <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
              {staffData.subjects.map(s => <span key={s} style={{ background:"#fff", border:"1px solid #90CAF9", color:"#0D47A1", fontSize:12, fontWeight:600, padding:"4px 14px", borderRadius:8 }}>{s}</span>)}
            </div>
          </div>
        )}
        {advisorClasses?.length > 0 && (
          <div style={{ background:"#E3F2FD", border:"1px solid #90CAF9", borderRadius:14, padding:"18px 20px", marginBottom:24 }}>
            <div style={{ fontSize:12, fontWeight:700, color:"#2196F3", marginBottom:12 }}>🏫 Class Advisor For</div>
            <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
              {advisorClasses.map(c => <span key={c.className} style={{ background:"#fff", border:"1px solid #90CAF9", color:"#2196F3", fontSize:12, fontWeight:600, padding:"4px 14px", borderRadius:8 }}>{c.className}</span>)}
            </div>
          </div>
        )}
        <div className="prof-grid">
          {fields.map(([k, v]) => (
            <div className="prof-field" key={k}>
              <div className="prof-field-label">{k}</div>
              <div className="prof-field-val">{v}</div>
            </div>
          ))}
        </div>
        <div className="prof-cred-box">
          <div className="prof-cred-title">🔐 Login Credentials</div>
          <div className="prof-cred-grid">
            <div className="prof-cred-card">
              <div className="prof-cred-key">Username</div>
              <div className="prof-cred-val">{staffData.username || "—"}</div>
            </div>
            <div className="prof-cred-card">
              <div className="prof-cred-key">Password</div>
              <div className="prof-cred-val">
                <span>{showPwd ? (staffData.passwordDisplay || staffData.password || "—") : "••••••••"}</span>
                <button onClick={() => setShowPwd(!showPwd)}
                  style={{ background:"none", border:"none", cursor:"pointer", fontSize:16, color:"#2196F3", padding:0 }}>
                  {showPwd ? "🙈" : "👁"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Timetable View ───────────────────────────────────────────────────────────
function TimetableView({ className }) {
  const [ttData,  setTtData]  = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState("");

  useEffect(() => {
    if (!className) return;
    setLoading(true); setError("");
    fetch(`https://schoolcrm-fzcv.onrender.com/api/timetables/${encodeURIComponent(className)}`)
      .then(r => { if (r.status === 404) throw new Error("notfound"); if (!r.ok) throw new Error("fail"); return r.json(); })
      .then(data => { setTtData(data); setLoading(false); })
      .catch(e  => { setError(e.message === "notfound" ? "notfound" : "error"); setLoading(false); });
  }, [className]);

  if (loading) return (
    <div className="tt-wrap">
      <div className="tt-header"><div><div className="tt-title">📅 Timetable — {className}</div></div></div>
      <div className="tt-empty">Loading timetable...</div>
    </div>
  );
  if (error === "notfound") return (
    <div className="tt-wrap">
      <div className="tt-header"><div><div className="tt-title">📅 Timetable — {className}</div><div className="tt-subtitle">No timetable assigned yet</div></div></div>
      <div className="tt-empty">
        <div style={{ fontSize:36, marginBottom:12 }}>📋</div>
        <div style={{ fontFamily:"'Playfair Display',serif", fontSize:16, fontWeight:700, color:"#0D47A1", marginBottom:6 }}>No Timetable Found</div>
        <div>Timetable for <strong>{className}</strong> has not been created yet.</div>
      </div>
    </div>
  );
  if (error) return (
    <div className="tt-wrap">
      <div className="tt-header"><div><div className="tt-title">📅 Timetable — {className}</div></div></div>
      <div className="tt-empty" style={{ color:"#2196F3" }}>Failed to load timetable.</div>
    </div>
  );

  const timetable = ttData?.timetable || {};
  const allPeriodIds = [...new Set(Object.values(timetable).flatMap(d => Object.keys(d || {})))].sort((a, b) => {
    const na = parseInt(a), nb = parseInt(b);
    return (!isNaN(na) && !isNaN(nb)) ? na - nb : a.localeCompare(b);
  });
  const activeDays  = DAYS.filter(d => timetable[d] && Object.keys(timetable[d]).length > 0);
  const displayDays = activeDays.length > 0 ? activeDays : DAYS;

  return (
    <div className="tt-wrap">
      <div className="tt-header">
        <div>
          <div className="tt-title">📅 Timetable — {className}</div>
          <div className="tt-subtitle">{ttData?.academicYear || "2025-26"} &nbsp;·&nbsp; {displayDays.length} working days &nbsp;·&nbsp; {allPeriodIds.length} periods/day</div>
        </div>
        {ttData?.updatedAt && (
          <div style={{ fontSize:10, color:"#90CAF9", textAlign:"right" }}>
            Last updated<br />
            <span style={{ color:"#E3F2FD" }}>{new Date(ttData.updatedAt).toLocaleDateString("en-IN", { day:"2-digit", month:"short", year:"numeric" })}</span>
          </div>
        )}
      </div>
      {allPeriodIds.length === 0 ? (
        <div className="tt-empty">Timetable exists but has no periods configured.</div>
      ) : (
        <div className="tt-grid-wrap">
          <table className="tt-table">
            <thead>
              <tr>
                <th>Day</th>
                {allPeriodIds.map(pid => <th key={pid}>Period {pid}</th>)}
              </tr>
            </thead>
            <tbody>
              {displayDays.map(day => (
                <tr key={day}>
                  <td>{day.slice(0, 3).toUpperCase()}</td>
                  {allPeriodIds.map(pid => {
                    const cell = timetable[day]?.[pid];
                    if (!cell) return <td key={pid}><span className="tt-empty-cell">—</span></td>;
                    const subj = cell.subject?.trim();
                    const teach = cell.teacher?.trim();
                    const isBreak = subj?.toLowerCase().includes("break") || subj?.toLowerCase().includes("lunch") || subj?.toLowerCase().includes("interval");
                    if (isBreak) return <td key={pid}><span className="tt-break-cell">{subj}</span></td>;
                    return (
                      <td key={pid}>
                        {subj
                          ? <div className="tt-cell"><span className="tt-subj">{subj}</span>{teach && <span className="tt-teach">{teach}</span>}</div>
                          : <span className="tt-empty-cell">—</span>
                        }
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Approval Panel ───────────────────────────────────────────────────────────
function ApprovalPanel({ advisorStaffId, onApprovalHandled }) {
  const API_BASE = process.env.REACT_APP_API_URL || "https://schoolcrm-fzcv.onrender.com";
  const [requests,    setRequests]    = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [acting,      setActing]      = useState({});

  const fetchPending = useCallback(async () => {
    if (!advisorStaffId) return;
    try {
      const res  = await fetch(`${API_BASE}/api/logbook-approvals/pending/${advisorStaffId}`);
      const data = await res.json();
      setRequests(Array.isArray(data) ? data : []);
    } catch { setRequests([]); }
    finally { setLoading(false); }
  }, [advisorStaffId, API_BASE]);

  useEffect(() => {
    fetchPending();
    const t1 = setInterval(fetchPending, 10000);
    return () => clearInterval(t1);
  }, [fetchPending]);

  const respond = async (id, action) => {
    setActing(p => ({ ...p, [id]: true }));
    try {
      await fetch(`${API_BASE}/api/logbook-approvals/${id}/respond`, {
        method:"PATCH", headers:{"Content-Type":"application/json"}, body:JSON.stringify({ action })
      });
      setRequests(prev => prev.filter(r => r._id !== id));
      if (onApprovalHandled) onApprovalHandled();
    } catch { alert("Failed to respond. Try again."); }
    finally { setActing(p => ({ ...p, [id]: false })); }
  };

  return (
    <div className="ap-wrap">
      <div className="ap-header">
        <div className="ap-title">🔑 LogBook Access Requests</div>
        <div className="ap-subtitle">Staff members waiting for your approval to access the LogBook</div>
      </div>
      <div style={{ padding:"20px 24px" }}>
        {loading && <div className="ap-empty">Loading requests...</div>}
        {!loading && requests.length === 0 && (
          <div className="ap-empty">
            <div style={{ fontSize:36, marginBottom:12 }}>✅</div>
            <div style={{ fontFamily:"'Playfair Display',serif", fontSize:16, fontWeight:700, color:"#0D47A1", marginBottom:6 }}>No Pending Requests</div>
            <div>All LogBook access requests have been handled.</div>
          </div>
        )}
        {!loading && requests.map(req => (
          <div className="ap-card" key={req._id}>
            <div style={{ width:42, height:42, borderRadius:10, background:"#E3F2FD", display:"flex", alignItems:"center", justifyContent:"center", fontWeight:700, fontSize:16, color:"#0D47A1", flexShrink:0 }}>
              {req.staffName?.charAt(0) || "?"}
            </div>
            <div className="ap-info">
              <div className="ap-name">{req.staffName}</div>
              <div className="ap-meta">
                <span style={{ background:"#E3F2FD", color:"#0D47A1", borderRadius:4, padding:"2px 8px", fontSize:10, fontWeight:600, marginRight:6 }}>{req.staffUserId}</span>
                requesting access to <span style={{ fontWeight:600, color:"#2196F3" }}>{req.className}</span> LogBook
              </div>
              <div style={{ fontSize:10, color:"#2196F3", marginTop:4 }}>
                Requested {new Date(req.requestedAt).toLocaleString("en-IN", { day:"2-digit", month:"short", hour:"2-digit", minute:"2-digit" })}
              </div>
            </div>
            <div className="ap-actions">
              <button className="ap-btn-approve" disabled={acting[req._id]} onClick={() => respond(req._id, "approved")}>
                {acting[req._id] ? "..." : "✓ Approve"}
              </button>
              <button className="ap-btn-reject" disabled={acting[req._id]} onClick={() => respond(req._id, "rejected")}>
                {acting[req._id] ? "..." : "✕ Reject"}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Parent Messages Panel ────────────────────────────────────────────────────
function ParentMessagesPanel({ advisorStaffId, onUnreadCountChange }) {
  const API_BASE = process.env.REACT_APP_API_URL || "https://schoolcrm-fzcv.onrender.com";
  const [messages,    setMessages]    = useState([]);
  const [msgsLoading, setMsgsLoading] = useState(true);
  const [replyingId,  setReplyingId]  = useState(null);
  const [replyText,   setReplyText]   = useState("");

  const fetchMessages = useCallback(async () => {
    if (!advisorStaffId) return;
    try {
      const res = await fetch(`${API_BASE}/api/logbook-messages/advisor/${advisorStaffId}`);
      const j   = await res.json();
      setMessages(Array.isArray(j) ? j : []);
      const unread = Array.isArray(j) ? j.filter(m => !m.read).length : 0;
      if (onUnreadCountChange) onUnreadCountChange(unread);
    } catch { setMessages([]); }
    finally { setMsgsLoading(false); }
  }, [advisorStaffId, API_BASE, onUnreadCountChange]);

  useEffect(() => {
    fetchMessages();
    const t2 = setInterval(fetchMessages, 10000);
    return () => clearInterval(t2);
  }, [fetchMessages]);

  const markMessageRead = async (id) => {
    try {
      await fetch(`${API_BASE}/api/logbook-messages/${id}/mark-read`, { method:"PATCH" });
      const updated = messages.map(m => m._id === id ? { ...m, read: true } : m);
      setMessages(updated);
      if (onUnreadCountChange) onUnreadCountChange(updated.filter(m => !m.read).length);
    } catch (err) { console.error("Failed to mark message read", err); }
  };

  const deleteMessage = async (id) => {
    try {
      const res = await fetch(`${API_BASE}/api/logbook-messages/${id}`, { method:"DELETE" });
      if (!res.ok) throw new Error("delete failed");
      const updated = messages.filter(m => m._id !== id);
      setMessages(updated);
      if (onUnreadCountChange) onUnreadCountChange(updated.filter(m => !m.read).length);
    } catch (err) { console.error("Failed to delete message", err); alert("Failed to delete message"); }
  };

  return (
    <div className="ap-wrap">
      <div className="ap-header">
        <div className="ap-title">📨 Parent Messages</div>
        <div className="ap-subtitle">Messages from parents and staff about students in your classes</div>
      </div>
      <div style={{ padding:"20px 24px" }}>
        {msgsLoading && <div style={{ color:"#2196F3" }}>Loading messages…</div>}
        {!msgsLoading && messages.length === 0 && (
          <div className="ap-empty">
            <div style={{ fontSize:36, marginBottom:12 }}>📭</div>
            <div style={{ fontFamily:"'Playfair Display',serif", fontSize:16, fontWeight:700, color:"#0D47A1", marginBottom:6 }}>No Messages</div>
            <div>No parent or staff messages yet.</div>
          </div>
        )}
        {!msgsLoading && messages.map(m => {
          const parts    = (m.className || "").split("-");
          const standard = parts[0] || "";
          const section  = parts[1] || "";
          return (
            <div key={m._id} style={{ display:"flex", gap:12, background:m.read?"#fff":"#E3F2FD", padding:14, borderRadius:14, marginBottom:12, alignItems:"flex-start", boxShadow:m.read?"none":"0 6px 18px rgba(13,71,161,0.06)", flexWrap:"wrap" }}>
              <div style={{ width:48, height:48, borderRadius:12, background:m.read?"#E3F2FD":"#90CAF9", display:"flex", alignItems:"center", justifyContent:"center", fontWeight:800, color:"#0D47A1", fontSize:16, flexShrink:0 }}>
                {m.parentName?.charAt(0) || "P"}
              </div>
              <div style={{ flex:1, minWidth:180 }}>
                <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                    <div style={{ fontWeight:800, color:"#0D2436", fontSize:14 }}>{m.studentName || "Student"}</div>
                    <div style={{ fontSize:11, color:"#2196F3", background:"#E3F2FD", padding:"3px 10px", borderRadius:40 }}>
                      {m.senderType === "staff" ? `Staff • ${m.senderName || "Teacher"}` : "Parent"}
                    </div>
                  </div>
                  <div style={{ fontSize:12, color:"#2196F3" }}>
                    {m.parentName ? `Parent: ${m.parentName}` : ""}
                    {m.parentContact ? ` · ${m.parentContact}` : ""}
                  </div>
                  {standard && (
                    <div style={{ fontSize:12, color:"#2196F3" }}>
                      {`${standard}${section ? ` • Sec ${section}` : ""}`}
                    </div>
                  )}
                </div>
                <div style={{ marginTop:8, fontSize:13, color:"#0D2436" }}>{m.message}</div>
                <div style={{ marginTop:6, fontSize:11, color:"#2196F3" }}>{new Date(m.createdAt).toLocaleString()}</div>
                {replyingId === m._id ? (
                  <div style={{ marginTop:8, display:"flex", gap:8, alignItems:"flex-start", flexWrap:"wrap" }}>
                    <textarea
                      value={replyText}
                      onChange={e => setReplyText(e.target.value)}
                      placeholder={`Reply to ${m.parentName}...`}
                      style={{ width:"100%", maxWidth:300, minHeight:70, padding:8, borderRadius:10, border:"1px solid #90CAF9", fontFamily:"'Inter',sans-serif", fontSize:13 }}
                    />
                    <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                      <button onClick={async () => {
                        if (!replyText.trim()) return;
                        try {
                          const payload = {
                            className:m.className, studentName:m.studentName||"",
                            parentName:m.parentName, parentContact:m.parentContact||"",
                            message:replyText, advisorStaffId,
                            senderType:"staff", senderId:advisorStaffId
                          };
                          const res = await fetch(`${API_BASE}/api/logbook-messages`, {
                            method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify(payload)
                          });
                          if (!res.ok) throw new Error("Failed");
                          setReplyText(""); setReplyingId(null); fetchMessages(); alert("Reply sent");
                        } catch (err) { console.error("Reply error", err); alert("Failed to send reply"); }
                      }} style={{ background:"#2196F3", color:"#fff", border:"none", padding:"8px 16px", borderRadius:40, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
                        Send Reply
                      </button>
                      <button onClick={() => { setReplyingId(null); setReplyText(""); }}
                        style={{ background:"#fff", border:"1px solid #90CAF9", padding:"8px 16px", borderRadius:40, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <button onClick={() => { setReplyingId(m._id); setReplyText(""); }}
                    style={{ marginTop:8, padding:"7px 16px", borderRadius:40, background:"#E3F2FD", border:"1px solid #90CAF9", cursor:"pointer", fontFamily:"'Inter',sans-serif", fontSize:12 }}>
                    💬 Reply
                  </button>
                )}
              </div>
              <div style={{ display:"flex", flexDirection:"column", gap:6, alignItems:"flex-end", flexShrink:0 }}>
                <button onClick={() => markMessageRead(m._id)} style={{
                  display:"flex", alignItems:"center", gap:6, padding:"8px 16px", borderRadius:40, border:"none",
                  background:m.read?"#E3F2FD":"linear-gradient(90deg,#0D47A1,#2196F3)",
                  color:m.read?"#0D47A1":"#fff", cursor:"pointer", fontSize:12, fontFamily:"'Inter',sans-serif"
                }}>
                  {m.read ? "✓ Read" : "📭 Mark read"}
                </button>
                <button onClick={() => { if (window.confirm("Delete this message?")) deleteMessage(m._id); }} style={{
                  display:"flex", alignItems:"center", gap:6, padding:"7px 14px", borderRadius:40,
                  border:"1px solid #E3F2FD", background:"#fff", color:"#4A6D8C", cursor:"pointer", fontSize:12, fontFamily:"'Inter',sans-serif"
                }}>
                  🗑️ Delete
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Approval Status Panel ────────────────────────────────────────────────────
function ApprovalStatusPanel({ advisorStaffId, className, API_BASE }) {
  const [allRecords, setAllRecords] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [filter,     setFilter]     = useState("all");
  const [search,     setSearch]     = useState("");

  const fetchAll = useCallback(async () => {
    if (!advisorStaffId) return;
    setLoading(true);
    try {
      const [pendingRes, historyRes] = await Promise.all([
        fetch(`${API_BASE}/api/logbook-approvals/pending/${advisorStaffId}`),
        fetch(`${API_BASE}/api/logbook-approvals/history/${advisorStaffId}`)
      ]);
      const pendingData = pendingRes.ok ? await pendingRes.json() : [];
      const historyData = historyRes.ok ? await historyRes.json() : [];
      const map = new Map();
      [
        ...(Array.isArray(pendingData) ? pendingData : []),
        ...(Array.isArray(historyData) ? historyData : [])
      ].forEach(r => map.set(r._id, r));
      const filtered = [...map.values()].filter(r => r.className === className);
      filtered.sort((a, b) => {
        if (a.status === "pending" && b.status !== "pending") return -1;
        if (b.status === "pending" && a.status !== "pending") return 1;
        return new Date(b.requestedAt) - new Date(a.requestedAt);
      });
      setAllRecords(filtered);
    } catch (err) { console.error("ApprovalStatus fetch error:", err); setAllRecords([]); }
    finally { setLoading(false); }
  }, [advisorStaffId, className, API_BASE]);

  useEffect(() => {
    fetchAll();
    const t = setInterval(fetchAll, 15000);
    return () => clearInterval(t);
  }, [fetchAll]);

  const displayed = allRecords.filter(r => {
    const matchFilter = filter === "all" || r.status === filter;
    const matchSearch = !search ||
      r.staffName?.toLowerCase().includes(search.toLowerCase()) ||
      r.staffUserId?.toLowerCase().includes(search.toLowerCase());
    return matchFilter && matchSearch;
  });

  const counts = {
    all:      allRecords.length,
    approved: allRecords.filter(r => r.status === "approved").length,
    rejected: allRecords.filter(r => r.status === "rejected").length,
    pending:  allRecords.filter(r => r.status === "pending").length,
  };
  const statusIcon  = { approved:"✓", rejected:"✕", pending:"⏳" };
  const statusLabel = { approved:"Approved", rejected:"Rejected", pending:"Pending" };
  const fmt = d => d
    ? new Date(d).toLocaleString("en-IN", { day:"2-digit", month:"short", year:"numeric", hour:"2-digit", minute:"2-digit" })
    : "—";

  return (
    <div className="as-wrap">
      <div className="as-header">
        <div>
          <div className="as-title">📊 Approval Status — {className}</div>
          <div className="as-subtitle">All staff LogBook access records for this class</div>
        </div>
        <div className="as-stats">
          <span className="as-stat">✓ {counts.approved} Approved</span>
          <span className="as-stat">✕ {counts.rejected} Rejected</span>
          {counts.pending > 0 && (
            <span className="as-stat">⏳ {counts.pending} Pending</span>
          )}
        </div>
      </div>
      <div className="as-filter-bar">
        {["all","approved","rejected","pending"].map(f => (
          <button key={f} className={`as-filter-btn${filter === f ? " active" : ""}`} onClick={() => setFilter(f)}>
            {f==="all"       ? `All (${counts.all})`
            :f==="approved"  ? `✓ Approved (${counts.approved})`
            :f==="rejected"  ? `✕ Rejected (${counts.rejected})`
                             : `⏳ Pending (${counts.pending})`}
          </button>
        ))}
        <div className="as-search">
          <span style={{ color:"#2196F3", fontSize:13 }}>🔍</span>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search staff name / ID..." />
          {search && <span onClick={() => setSearch("")} style={{ cursor:"pointer", color:"#2196F3", fontSize:12 }}>✕</span>}
        </div>
      </div>
      {loading && <div className="as-empty">Loading approval records...</div>}
      {!loading && displayed.length === 0 && (
        <div className="as-empty">
          <div style={{ fontSize:36, marginBottom:12 }}>
            {filter==="all"?"📭":filter==="approved"?"✅":filter==="rejected"?"❌":"⏳"}
          </div>
          <div style={{ fontFamily:"'Playfair Display',serif", fontSize:16, fontWeight:700, color:"#0D47A1", marginBottom:6 }}>
            {allRecords.length === 0 ? "No Requests Yet" : `No ${filter === "all" ? "" : filter} records`}
          </div>
          <div style={{ color:"#2196F3", fontSize:13 }}>
            {allRecords.length === 0
              ? `No staff have requested LogBook access for ${className} yet.`
              : `No ${filter} records match your search.`}
          </div>
        </div>
      )}
      {!loading && displayed.length > 0 && (
        <div className="as-table-wrap">
          <table className="as-table">
            <thead>
              <tr>
                <th>#</th><th>Staff Member</th><th>Staff ID</th><th>Status</th><th>Requested At</th><th>Responded At</th>
              </tr>
            </thead>
            <tbody>
              {displayed.map((rec, idx) => {
                const initials = rec.staffName
                  ? rec.staffName.trim().split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase()
                  : "?";
                const avatarC = rec.status === "approved"
                  ? { bg:"#E3F2FD", color:"#0D47A1" }
                  : rec.status === "rejected"
                    ? { bg:"#E3F2FD", color:"#4A6D8C" }
                    : { bg:"#90CAF9", color:"#0D47A1" };
                return (
                  <tr key={rec._id}>
                    <td style={{ color:"#2196F3", fontSize:12 }}>{idx + 1}</td>
                    <td>
                      <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                        <div style={{ width:34, height:34, borderRadius:8, background:avatarC.bg, color:avatarC.color, display:"flex", alignItems:"center", justifyContent:"center", fontWeight:700, fontSize:14, flexShrink:0 }}>{initials}</div>
                        <div>
                          <div style={{ fontWeight:700, fontSize:13, color:"#0D2436" }}>{rec.staffName || "Unknown Staff"}</div>
                          <div style={{ fontSize:11, color:"#2196F3", marginTop:2 }}>{rec.className}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span style={{ background:"#E3F2FD", color:"#0D47A1", borderRadius:6, padding:"3px 10px", fontSize:11, fontWeight:700 }}>
                        {rec.staffUserId || "—"}
                      </span>
                    </td>
                    <td><span className={`as-badge ${rec.status}`}>{statusIcon[rec.status]} {statusLabel[rec.status]}</span></td>
                    <td style={{ fontSize:12, color:"#2196F3" }}>{fmt(rec.requestedAt)}</td>
                    <td style={{ fontSize:12, color:"#2196F3" }}>
                      {rec.respondedAt
                        ? fmt(rec.respondedAt)
                        : <span style={{ color:"#90CAF9", fontStyle:"italic" }}>Not yet</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <div style={{ padding:"12px 20px", borderTop:"1px solid #E3F2FD", display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:8 }}>
        <span style={{ fontSize:11, color:"#90CAF9" }}>Auto-refreshes every 15 seconds</span>
        <button onClick={fetchAll} style={{ background:"#E3F2FD", border:"1px solid #90CAF9", borderRadius:40, padding:"5px 16px", fontSize:12, color:"#2196F3", cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
          🔄 Refresh Now
        </button>
      </div>
    </div>
  );
}

// ─── Circulars Panel ──────────────────────────────────────────────────────────
function CircularsPanel({ API_BASE, onClose, readCircularIds, onMarkRead }) {
  const [circulars, setCirculars] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/circulars`);
        const j   = await res.json();
        if (j.success) {
          const staffCirculars = (j.circulars || []).filter(
            c => !c.isDraft && c.recipientsMeta?.allStaff === true
          );
          setCirculars(staffCirculars);
        } else {
          setCirculars([]);
        }
      } catch (err) { console.error("Failed to load circulars", err); setCirculars([]); }
      finally { setLoading(false); }
    })();
  }, [API_BASE]);

  const filteredCirculars = useMemo(() => {
    let result = circulars;
    if (searchQuery.trim() !== "") {
      const q = searchQuery.trim().toLowerCase();
      result = result.filter(c => 
        c.title?.toLowerCase().includes(q) || 
        c.message?.toLowerCase().includes(q)
      );
    }
    if (filterType !== "all") {
      result = result.filter(c => {
        const meta = c.recipientsMeta || {};
        if (filterType === "staff") return meta.allStaff === true;
        return true;
      });
    }
    if (startDate) {
      const start = new Date(startDate);
      result = result.filter(c => new Date(c.createdAt) >= start);
    }
    if (endDate) {
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      result = result.filter(c => new Date(c.createdAt) <= end);
    }
    return result;
  }, [circulars, searchQuery, filterType, startDate, endDate]);

  return (
    <div>
      <div style={{ display:"flex", flexDirection:"column", gap:12, marginBottom:16, flexWrap:"wrap" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:10 }}>
          <div>
            <div style={{ fontWeight:700, fontSize:18, fontFamily:"'Playfair Display',serif", color:"#0D2436" }}>Circulars</div>
            <div style={{ fontSize:12, color:"#2196F3" }}>
              {filteredCirculars.length} of {circulars.length} items
            </div>
          </div>
          <button onClick={onClose} style={{ padding:"8px 16px", borderRadius:40, border:"1px solid #90CAF9", background:"#fff", cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
            Close
          </button>
        </div>

        <div style={{ display:"flex", gap:10, flexWrap:"wrap", alignItems:"center", background:"#fff", padding:"10px 14px", borderRadius:12, border:"1px solid #90CAF9" }}>
          <input
            type="text"
            placeholder="Search circulars..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            style={{ flex:1, minWidth:150, padding:"7px 14px", borderRadius:40, border:"1px solid #90CAF9", fontFamily:"'Inter',sans-serif", fontSize:13, outline:"none" }}
          />
          <select
            value={filterType}
            onChange={e => setFilterType(e.target.value)}
            style={{ padding:"7px 14px", borderRadius:40, border:"1px solid #90CAF9", fontFamily:"'Inter',sans-serif", fontSize:13, cursor:"pointer", outline:"none", background:"#fff" }}
          >
            <option value="all">All (Staff Circulars)</option>
            <option value="staff">All Staff</option>
          </select>
          <div style={{ display:"flex", gap:6, alignItems:"center", flexWrap:"wrap" }}>
            <span style={{ fontSize:12, color:"#2196F3", fontWeight:600 }}>From:</span>
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
              style={{ padding:"5px 10px", borderRadius:40, border:"1px solid #90CAF9", fontFamily:"'Inter',sans-serif", fontSize:12, outline:"none" }} />
            <span style={{ fontSize:12, color:"#2196F3", fontWeight:600 }}>To:</span>
            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
              style={{ padding:"5px 10px", borderRadius:40, border:"1px solid #90CAF9", fontFamily:"'Inter',sans-serif", fontSize:12, outline:"none" }} />
          </div>
          {(searchQuery || filterType !== "all" || startDate || endDate) && (
            <button onClick={() => { setSearchQuery(""); setFilterType("all"); setStartDate(""); setEndDate(""); }}
              style={{ padding:"6px 14px", borderRadius:40, background:"#E3F2FD", border:"1px solid #90CAF9", cursor:"pointer", fontSize:12, fontFamily:"'Inter',sans-serif", color:"#2196F3" }}>
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {loading && (
        <div style={{ padding:24, background:"#fff", borderRadius:12, border:"1px solid #90CAF9", textAlign:"center", color:"#2196F3" }}>
          Loading circulars...
        </div>
      )}
      {!loading && circulars.length === 0 && (
        <div style={{ padding:24, background:"#fff", borderRadius:12, border:"1px solid #90CAF9" }}>
          No circulars available.
        </div>
      )}
      {!loading && filteredCirculars.length === 0 && circulars.length > 0 && (
        <div style={{ padding:24, background:"#fff", borderRadius:12, border:"1px solid #90CAF9", textAlign:"center", color:"#2196F3" }}>
          No circulars match your search or filter criteria.
        </div>
      )}
      {!loading && filteredCirculars.length > 0 && (
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(260px,1fr))", gap:14 }}>
          {filteredCirculars.map(c => {
            const isRead = readCircularIds?.has ? readCircularIds.has(c._id) : false;
            return (
              <div key={c._id} style={{
                background:"#fff", borderRadius:14, padding:18,
                boxShadow: isRead ? "0 1px 6px rgba(13,71,161,0.04)" : "0 4px 16px rgba(13,71,161,0.08)",
                border: isRead ? "1px solid #E3F2FD" : "1px solid #90CAF9"
              }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:8, marginBottom:6 }}>
                  <div style={{ fontWeight:700, fontSize:14, fontFamily:"'Playfair Display',serif", color:"#0D2436" }}>{c.title}</div>
                  {!isRead && (
                    <span style={{ width:8, height:8, borderRadius:"50%", background:"#2196F3", flexShrink:0, marginTop:4 }} title="Unread" />
                  )}
                </div>
                <div style={{ fontSize:12, color:"#2196F3", marginBottom:8 }}>
                  {c.isDraft ? "Draft" : "Sent"} · {new Date(c.createdAt).toLocaleString()}
                </div>
                <div style={{ fontSize:13, color:"#0D2436", whiteSpace:"pre-wrap", marginBottom:12 }}>{c.message}</div>
                {isRead ? (
                  <div style={{ display:"flex", alignItems:"center", gap:5, fontSize:12, color:"#0D47A1", fontWeight:600 }}>
                    ✓ Read
                  </div>
                ) : (
                  <button onClick={() => onMarkRead && onMarkRead(c._id)}
                    style={{ padding:"6px 16px", borderRadius:40, border:"1px solid #2196F3", background:"#2196F3", color:"#fff", fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
                    Mark as read
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── EditStudentModal ──────────────────────────────────────────────────────────
function EditStudentModal({ student, API_BASE, onClose, onSaved }) {
  const FIELD_SECTIONS = [
    {
      section: "🧑 Personal Information",
      items: [
        { key:"studentName",  label:"Full Name",     type:"text" },
        { key:"dob",          label:"Date of Birth", type:"date" },
        { key:"gender",       label:"Gender",        type:"select", options:["Male","Female","Other"] },
        { key:"bloodGroup",   label:"Blood Group",   type:"select", options:["A+","A-","B+","B-","AB+","AB-","O+","O-"] },
        { key:"aadhaar",      label:"Aadhaar No",    type:"text" },
        { key:"motherTongue", label:"Mother Tongue", type:"text" },
        { key:"nationality",  label:"Nationality",   type:"text" },
        { key:"religion",     label:"Religion",      type:"text" },
      ],
    },
    {
      section: "🎓 Academic Details",
      items: [
        { key:"rollNo",        label:"Roll Number",     type:"text" },
        { key:"applyingClass", label:"Class",           type:"text", readOnly:true },
        { key:"section",       label:"Section",         type:"text", readOnly:true },
        { key:"medium",        label:"Medium",          type:"text" },
        { key:"group",         label:"Group",           type:"text" },
        { key:"prevSchool",    label:"Previous School", type:"text" },
        { key:"prevClass",     label:"Previous Class",  type:"text" },
        { key:"prevBoard",     label:"Previous Board",  type:"text" },
        { key:"tcNumber",      label:"TC Number",       type:"text" },
      ],
    },
    {
      section: "👨‍👩‍👧 Parent & Contact",
      items: [
        { key:"fatherName",       label:"Father's Name",       type:"text" },
        { key:"fatherOccupation", label:"Father's Occupation", type:"text" },
        { key:"fatherMobile",     label:"Father's Mobile",     type:"text" },
        { key:"motherName",       label:"Mother's Name",       type:"text" },
        { key:"motherOccupation", label:"Mother's Occupation", type:"text" },
        { key:"motherMobile",     label:"Mother's Mobile",     type:"text" },
        { key:"address",          label:"Address",             type:"text" },
        { key:"city",             label:"City",                type:"text" },
        { key:"pincode",          label:"Pincode",             type:"text" },
      ],
    },
  ];

  const [form, setForm] = useState(() => {
    const init = {};
    FIELD_SECTIONS.forEach(sec =>
      sec.items.forEach(f => {
        init[f.key] = (student[f.key] !== undefined && student[f.key] !== null)
          ? String(student[f.key])
          : "";
      })
    );
    return init;
  });

  const [saving,       setSaving]       = useState(false);
  const [error,        setError]        = useState("");
  const [photoPreview, setPhotoPreview] = useState(student.photo || null);
  const photoInputRef = useRef();

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { alert("Photo must be under 2 MB."); return; }
    const reader = new FileReader();
    reader.onload = (ev) => setPhotoPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    const id = student._id;
    if (!id) { setError("❌ Student ID missing — cannot save."); return; }
    setSaving(true);
    setError("");
    try {
      const payload = { ...form };
      payload.photo = (photoPreview !== null && photoPreview !== undefined) ? photoPreview : "";
      const res = await fetch(`${API_BASE}/api/admissions/${id}`, {
        method:  "PATCH",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify(payload),
      });
      if (!res.ok) {
        let msg = `HTTP ${res.status}`;
        try { const body = await res.json(); msg = body.message || msg; } catch {}
        throw new Error(msg);
      }
      const savedDoc = await res.json();
      onSaved(savedDoc);
    } catch (err) {
      console.error("EditStudentModal save error:", err);
      setError(`❌ Failed to save: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="esm-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="esm-modal">
        <div className="esm-header">
          <div className="esm-title">✏️ Edit — {student.studentName}</div>
          <button className="esm-close" onClick={onClose}>✕</button>
        </div>
        <div className="esm-body">
          <div className="esm-section">
            <div className="esm-section-title">📸 Student Photo</div>
            <div className="esm-photo-area" onClick={() => photoInputRef.current?.click()}>
              {photoPreview
                ? <img src={photoPreview} alt="Student" className="esm-photo-preview" />
                : <div className="esm-photo-placeholder" style={{ width:90, height:90, borderRadius:16, background:"#E3F2FD", display:"flex", alignItems:"center", justifyContent:"center", fontSize:36, border:"3px solid #90CAF9" }}>📷</div>
              }
              <div className="esm-photo-hint">
                {photoPreview ? "Click to change photo" : "Click to upload photo"}
              </div>
              <div className="esm-photo-sub">JPG / PNG · Max 2 MB</div>
              <input ref={photoInputRef} type="file" accept="image/*" style={{ display:"none" }} onChange={handlePhotoChange} />
            </div>
            {photoPreview && (
              <button onClick={() => setPhotoPreview(null)}
                style={{ marginTop:8, fontSize:11, color:"#2196F3", background:"none", border:"none", cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
                🗑 Remove photo
              </button>
            )}
          </div>
          {FIELD_SECTIONS.map(sec => (
            <div className="esm-section" key={sec.section}>
              <div className="esm-section-title">{sec.section}</div>
              <div className="esm-grid">
                {sec.items.map(f => (
                  <div className="esm-field" key={f.key}>
                    <label className="esm-label">{f.label}</label>
                    {f.type === "select" ? (
                      <select className="esm-select" value={form[f.key] || ""} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}>
                        <option value="">— Select —</option>
                        {f.options.map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    ) : (
                      <input className={`esm-input${f.readOnly ? " esm-input-readonly" : ""}`} type={f.type} value={form[f.key] || ""} readOnly={!!f.readOnly} placeholder={f.label} onChange={e => { if (f.readOnly) return; setForm(p => ({ ...p, [f.key]: e.target.value })); }} />
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
          {error && <div className="esm-error" style={{ color:"#2196F3", fontSize:13, fontWeight:600, marginTop:6, padding:"10px 14px", background:"#E3F2FD", border:"1px solid #90CAF9", borderRadius:8 }}>{error}</div>}
        </div>
        <div className="esm-footer">
          <button className="esm-cancel-btn" onClick={onClose}>Cancel</button>
          <button className="esm-save-btn" onClick={handleSave} disabled={saving}>
            {saving ? "⏳ Saving…" : "💾 Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── StudentDetailsPanel ──────────────────────────────────────────────────────
function StudentDetailsPanel({ className, API_BASE, staffId, staffData }) {
  const navigate = useNavigate();
  const [students,      setStudents]      = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [search,        setSearch]        = useState("");
  const [editingId,     setEditingId]     = useState(null);
  const [localRolls,    setLocalRolls]    = useState({});
  const [hasUnsaved,    setHasUnsaved]    = useState(false);
  const [savingAll,     setSavingAll]     = useState(false);
  const [settingRolls,  setSettingRolls]  = useState(false);
  const [rollMsg,       setRollMsg]       = useState("");
  const [editStudent,   setEditStudent]   = useState(null);
  const [showMarksheet, setShowMarksheet] = useState(false);

  const rollPrefix = useMemo(() => generateRollPrefix(className), [className]);

  const analysisParams = useMemo(() => {
    if (!className) return { cls: "", section: "", group: "" };
    const dashIdx = className.lastIndexOf("-");
    const section = dashIdx >= 0 ? className.slice(dashIdx + 1).trim() : "";
    const before  = dashIdx >= 0 ? className.slice(0, dashIdx).trim() : className;
    const parts   = before.split(" ");
    if (parts[1] === "11" || parts[1] === "12") {
      return { cls: `Class ${parts[1]}`, section, group: parts.slice(2).join(" ") || "" };
    }
    return { cls: before, section, group: "" };
  }, [className]);

  const goToStudentAnalysis = () => {
    const { cls, section, group } = analysisParams;
    navigate(`/student-analysis?cls=${encodeURIComponent(cls)}&section=${encodeURIComponent(section)}&group=${encodeURIComponent(group)}`);
  };

  const fetchStudents = useCallback(async () => {
    setLoading(true);
    setLocalRolls({});
    setHasUnsaved(false);
    setEditingId(null);
    try {
      const res  = await fetch(`${API_BASE}/api/admissions/by-class/${encodeURIComponent(className)}`);
      if (!res.ok) throw new Error("Failed");
      const data = await res.json();
      const sorted = (Array.isArray(data) ? data : []).sort((a, b) =>
        (a.studentName || "").localeCompare(b.studentName || "")
      );
      setStudents(sorted);
    } catch {
      setStudents([]);
    } finally {
      setLoading(false);
    }
  }, [className, API_BASE]);

  useEffect(() => { fetchStudents(); }, [fetchStudents]);

  const getRoll = (stu) =>
    localRolls[stu._id] !== undefined ? localRolls[stu._id] : (stu.rollNo ?? "");

  const handleRollChange = (stuId, value) => {
    setLocalRolls(prev => ({ ...prev, [stuId]: value }));
    setHasUnsaved(true);
  };

  const commitRollEdit = () => setEditingId(null);

  const handleSaveAllRolls = async () => {
    if (!hasUnsaved) return;
    const changedIds = Object.keys(localRolls);
    if (changedIds.length === 0) { setHasUnsaved(false); return; }
    setSavingAll(true);
    setRollMsg("");
    try {
      const results = await Promise.allSettled(
        changedIds.map(id =>
          fetch(`${API_BASE}/api/admissions/${id}`, {
            method:  "PATCH",
            headers: { "Content-Type": "application/json" },
            body:    JSON.stringify({ rollNo: localRolls[id] }),
          })
        )
      );
      const failed = results.filter(r => r.status === "rejected" || (r.value && !r.value.ok)).length;
      if (failed > 0) {
        setRollMsg(`⚠️ ${failed} of ${changedIds.length} failed to save.`);
      } else {
        setStudents(prev =>
          prev.map(s => localRolls[s._id] !== undefined ? { ...s, rollNo: localRolls[s._id] } : s)
        );
        setLocalRolls({});
        setHasUnsaved(false);
        setRollMsg(`✅ ${changedIds.length} roll number(s) saved!`);
      }
    } catch {
      setRollMsg("❌ Save failed. Please try again.");
    } finally {
      setSavingAll(false);
      setTimeout(() => setRollMsg(""), 4000);
    }
  };

  const handleSetRollNumbers = async () => {
    if (students.length === 0) return;
    const example1 = formatRollNumber(rollPrefix, 1);
    const example2 = formatRollNumber(rollPrefix, 2);
    if (!window.confirm(
      `Assign roll numbers (${example1}, ${example2}...) alphabetically for ${className}?\n\nThis will overwrite existing roll numbers in the database.\n\nProceed?`
    )) return;

    setSettingRolls(true);
    setRollMsg("");
    const sorted = [...students].sort((a, b) => (a.studentName || "").localeCompare(b.studentName || ""));
    const newRolls = {};
    sorted.forEach((stu, i) => { newRolls[stu._id] = formatRollNumber(rollPrefix, i + 1); });

    try {
      const results = await Promise.allSettled(
        Object.entries(newRolls).map(([id, rollNo]) =>
          fetch(`${API_BASE}/api/admissions/${id}`, {
            method:  "PATCH",
            headers: { "Content-Type": "application/json" },
            body:    JSON.stringify({ rollNo }),
          })
        )
      );
      const failed = results.filter(r => r.status === "rejected" || (r.value && !r.value.ok)).length;
      if (failed > 0) {
        setRollMsg(`⚠️ ${failed} updates failed.`);
        const successIds = Object.keys(newRolls).filter(
          (_, i) => results[i].status !== "rejected" && results[i].value?.ok
        );
        setStudents(prev =>
          prev.map(s => successIds.includes(s._id) ? { ...s, rollNo: newRolls[s._id] } : s)
        );
      } else {
        setStudents(prev =>
          prev.map(s => newRolls[s._id] !== undefined ? { ...s, rollNo: newRolls[s._id] } : s)
        );
        setLocalRolls({});
        setHasUnsaved(false);
        setRollMsg(`✅ Roll numbers saved for ${sorted.length} students!`);
      }
    } catch {
      setRollMsg("❌ Failed to set roll numbers.");
    } finally {
      setSettingRolls(false);
      setTimeout(() => setRollMsg(""), 5000);
    }
  };

  const displayed = students.filter(s =>
    !search ||
    (s.studentName || "").toLowerCase().includes(search.toLowerCase()) ||
    String(getRoll(s) || "").toLowerCase().includes(search.toLowerCase())
  );

  const genderStyle = g =>
    g?.toLowerCase() === "male"
      ? { background:"#E3F2FD", color:"#0D47A1" }
      : { background:"#E3F2FD", color:"#2196F3" };

  return (
    <div className="sd-wrap">
      <div className="sd-header">
        <div>
          <div className="sd-title">👩‍🎓 Student Details — {className}</div>
          <div className="sd-subtitle">
            {loading
              ? "Loading..."
              : `${students.length} students enrolled · Roll format: ${formatRollNumber(rollPrefix, 1)}`}
          </div>
        </div>
        <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:6 }}>
          <button className="sd-roll-btn" onClick={goToStudentAnalysis} disabled={loading || students.length === 0}>
            📊 Student Analysis
          </button>
          <button className="sd-roll-btn" onClick={handleSetRollNumbers} disabled={settingRolls || loading || students.length === 0}>
            {settingRolls ? "⏳ Assigning..." : "🔢 Set Roll Numbers (A→Z)"}
          </button>
          {rollMsg && (
            <span style={{ fontSize:12, fontWeight:600, color: rollMsg.startsWith("✅") ? "#0D47A1" : rollMsg.startsWith("⚠") ? "#2196F3" : "#0D47A1" }}>
              {rollMsg}
            </span>
          )}
        </div>
      </div>

      {hasUnsaved && (
        <div style={{ background:"#E3F2FD", border:"1px solid #90CAF9", borderRadius:10, padding:"10px 16px", margin:"12px 20px 0", display:"flex", alignItems:"center", justifyContent:"space-between", gap:12, fontSize:12, color:"#0D47A1", fontWeight:600, flexWrap:"wrap" }}>
          <span>⚠️ You have unsaved roll number changes</span>
          <div style={{ display:"flex", gap:8 }}>
            <button className="sd-roll-btn" onClick={handleSaveAllRolls} disabled={savingAll} style={{ padding:"6px 16px", fontSize:11 }}>
              {savingAll ? "⏳ Saving..." : "💾 Save All Changes"}
            </button>
            <button onClick={() => { setLocalRolls({}); setHasUnsaved(false); setEditingId(null); }}
              style={{ background:"#fff", color:"#0D47A1", border:"1px solid #90CAF9", padding:"6px 16px", borderRadius:40, fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
              ✕ Discard
            </button>
          </div>
        </div>
      )}

      <div className="sd-toolbar">
        <div className="sd-search-box">
          <span style={{ color:"#2196F3", fontSize:14 }}>🔍</span>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name or roll no…" />
          {search && <span onClick={() => setSearch("")} style={{ cursor:"pointer", color:"#2196F3", fontSize:12, flexShrink:0 }}>✕</span>}
        </div>
        <div style={{ fontSize:12, color:"#2196F3", marginLeft:"auto", whiteSpace:"nowrap" }}>
          {displayed.length} of {students.length} shown
        </div>
      </div>

      {loading ? (
        <div className="sd-empty">
          <div style={{ fontSize:32, marginBottom:10 }}>⏳</div>
          <div style={{ fontFamily:"'Playfair Display',serif", fontWeight:700, color:"#0D47A1" }}>Loading students…</div>
        </div>
      ) : displayed.length === 0 ? (
        <div className="sd-empty">
          <div style={{ fontSize:36, marginBottom:10 }}>📭</div>
          <div style={{ fontFamily:"'Playfair Display',serif", fontSize:16, fontWeight:700, color:"#0D47A1", marginBottom:6 }}>
            {students.length === 0 ? "No Students Found" : `No match for "${search}"`}
          </div>
          <div style={{ fontSize:13, color:"#2196F3" }}>
            {students.length === 0
              ? `No admitted students found for ${className}.`
              : "Try a different search term."}
          </div>
        </div>
      ) : (
        <div className="sd-table-wrap">
          <table className="sd-table">
            <thead>
              <tr>
                <th style={{ width:42 }}>#</th>
                <th style={{ width:60 }}>Photo</th>
                <th style={{ width:130 }}>Roll No</th>
                <th>Name</th>
                <th style={{ width:90 }}>Gender</th>
                <th style={{ width:110 }}>DOB</th>
                <th style={{ width:72 }}>Blood</th>
                <th>Father</th>
                <th style={{ width:130 }}>Mobile</th>
                <th style={{ width:80 }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {displayed.map((stu, i) => {
                const origIdx     = students.findIndex(s => s._id === stu._id);
                const ac          = avatarColor(origIdx >= 0 ? origIdx : i);
                const initial     = (stu.studentName || "S").charAt(0).toUpperCase();
                const isEditing   = editingId === stu._id;
                const currentRoll = getRoll(stu);
                const isModified  = localRolls[stu._id] !== undefined;

                return (
                  <tr key={stu._id}>
                    <td style={{ color:"#2196F3", fontSize:12 }}>{i + 1}</td>
                    <td>
                      {stu.photo
                        ? <img src={stu.photo} alt={stu.studentName} style={{ width:38, height:38, borderRadius:10, objectFit:"cover", border:"2px solid #90CAF9", flexShrink:0 }} />
                        : <div style={{ width:38, height:38, borderRadius:10, display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'Playfair Display',serif", fontWeight:800, fontSize:14, flexShrink:0, border:"2px solid transparent", background:ac.bg, color:ac.color }}>{initial}</div>
                      }
                    </td>
                    <td onClick={e => e.stopPropagation()}>
                      {isEditing ? (
                        <input className="sd-roll-input" type="text" autoFocus value={currentRoll}
                          onChange={e => handleRollChange(stu._id, e.target.value)}
                          onKeyDown={e => { if (e.key === "Enter" || e.key === "Tab") commitRollEdit(); if (e.key === "Escape") setEditingId(null); }}
                          onBlur={commitRollEdit} placeholder={formatRollNumber(rollPrefix, i + 1)}
                          style={{ width:110, padding:"5px 10px", border:"1.5px solid #0D47A1", borderRadius:8, fontSize:12, fontWeight:700, color:"#0D47A1", background:"#E3F2FD", fontFamily:"'Inter',sans-serif", textAlign:"center", outline:"none", boxShadow:"0 0 0 3px rgba(13,71,161,0.08)" }} />
                      ) : (
                        <span title="Click to edit roll number" onClick={() => setEditingId(stu._id)}
                          style={{ display:"inline-block", background: isModified ? "#E3F2FD" : "#E3F2FD", color: isModified ? "#0D47A1" : "#0D47A1", borderRadius:8, padding:"3px 12px", fontSize:11, fontWeight:800, cursor:"text", minWidth:76, textAlign:"center", border: isModified ? "1.5px dashed #90CAF9" : "1.5px dashed #E3F2FD", letterSpacing:"0.3px" }}>
                          {currentRoll || "—"}
                          {isModified && <span style={{ fontSize:9, marginLeft:4, opacity:0.7 }}>*</span>}
                        </span>
                      )}
                    </td>
                    <td><span style={{ fontWeight:600, color:"#0D2436" }}>{stu.studentName || "—"}</span></td>
                    <td>
                      {stu.gender
                        ? <span style={{ ...genderStyle(stu.gender), borderRadius:20, padding:"2px 12px", fontSize:11, fontWeight:700 }}>{stu.gender}</span>
                        : <span style={{ color:"#90CAF9" }}>—</span>
                      }
                    </td>
                    <td style={{ fontSize:12, color:"#2196F3" }}>{stu.dob || <span style={{ color:"#90CAF9" }}>—</span>}</td>
                    <td>{stu.bloodGroup ? <span style={{ color:"#2196F3", fontWeight:700, fontSize:13 }}>{stu.bloodGroup}</span> : <span style={{ color:"#90CAF9" }}>—</span>}</td>
                    <td style={{ fontSize:12, color:"#2196F3" }}>{stu.fatherName || <span style={{ color:"#90CAF9" }}>—</span>}</td>
                    <td style={{ fontSize:12, color:"#2196F3" }}>{stu.fatherMobile || <span style={{ color:"#90CAF9" }}>—</span>}</td>
                    <td onClick={e => e.stopPropagation()}>
                      <button className="sd-act-btn" style={{ padding:"5px 14px", borderRadius:8, fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"'Inter',sans-serif", border:"none", transition:"all .15s", whiteSpace:"nowrap", background:"#E3F2FD", color:"#0D47A1" }} onClick={() => setEditStudent(stu)}>
                        ✏️ Edit
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editStudent && (
        <EditStudentModal
          student={editStudent}
          API_BASE={API_BASE}
          onClose={() => setEditStudent(null)}
          onSaved={updated => {
            setStudents(prev => prev.map(s => s._id === updated._id ? updated : s));
            setEditStudent(null);
          }}
        />
      )}

      {showMarksheet && (
        <Marksheet
          className={className}
          API_BASE={API_BASE}
          staffId={staffId}
          staffData={staffData}
          onClose={() => setShowMarksheet(false)}
        />
      )}
    </div>
  );
}

// ─── Sidebar Tree ──────────────────────────────────────────────────────────────
function SidebarTree({
  staffClasses, onSelect, selectedClass, selectedView,
  onViewSelect, loading, advisorStaffId, pendingCount,
  onPendingCountChange, navigate,
  onItemClick, unreadCircularsCount, unreadCount
}) {
  const [openCls, setOpenCls] = useState({});

  useEffect(() => {
    if (!advisorStaffId) return;
    const poll = async () => {
      try {
        const res  = await fetch(`https://schoolcrm-fzcv.onrender.com/api/logbook-approvals/pending/${advisorStaffId}`);
        const data = await res.json();
        if (onPendingCountChange) onPendingCountChange(Array.isArray(data) ? data.length : 0);
      } catch {}
    };
    poll();
    const t = setInterval(poll, 10000);
    return () => clearInterval(t);
  }, [advisorStaffId, onPendingCountChange]);

  const groupedClasses = {};
  staffClasses.forEach(cls => {
    const standard = cls.className.split("-")[0];
    if (!groupedClasses[standard]) groupedClasses[standard] = [];
    groupedClasses[standard].push(cls);
  });

  const tap = (fn) => { fn(); if (onItemClick) onItemClick(); };

  if (loading) return (
    <div className="sidebar-tree">
      <div style={{ padding:"20px", textAlign:"center", color:"#2196F3" }}>Loading...</div>
    </div>
  );

  return (
    <div className="sidebar-tree">
      <div className={`tree-profile-row${selectedView === "profile" ? " active" : ""}`}
        onClick={() => tap(() => onViewSelect(null, "profile"))}>
        <span style={{ fontSize:16 }}>👤</span>
        <span style={{ fontSize:13, fontWeight:600, color:"#2196F3", flex:1 }}>My Profile</span>
        <span style={{ fontSize:10, color:"#2196F3" }}>View →</span>
      </div>

      <div className={`tree-circulars-row${selectedView === "circulars" ? " active" : ""}`}
        onClick={() => tap(() => onViewSelect(null, "circulars"))}>
        <span style={{ fontSize:16, position:"relative" }}>
          📣
          {unreadCircularsCount > 0 && (
            <span style={{ position:"absolute", top:-6, right:-8, minWidth:16, height:16, padding:"0 4px", borderRadius:9, background:"#2196F3", color:"#fff", fontSize:9, fontWeight:700, display:"flex", alignItems:"center", justifyContent:"center", border:"2px solid #fff", boxShadow:"0 2px 6px rgba(33,150,243,0.4)" }}>
              {unreadCircularsCount > 9 ? "9+" : unreadCircularsCount}
            </span>
          )}
        </span>
        <span style={{ fontSize:13, fontWeight:600, color:"#2196F3", flex:1 }}>Circulars</span>
        {unreadCircularsCount > 0
          ? <span className="tree-badge">{unreadCircularsCount} new</span>
          : <span style={{ fontSize:10, color:"#2196F3" }}>View →</span>}
      </div>

      <div className="tree-dailyatt-row" onClick={() => tap(() => navigate("/staff-daily-attendance"))}>
        <span style={{ fontSize:16 }}>📆</span>
        <span className="tree-dailyatt-label">Daily Attendance</span>
        <span style={{ fontSize:10, color:"#2196F3" }}>Take →</span>
      </div>

      {staffClasses.length === 0 && (
        <div style={{ padding:"20px", textAlign:"center", color:"#2196F3", fontSize:13 }}>
          No classes assigned as Class Advisor
        </div>
      )}

      {Object.keys(groupedClasses).sort().map(standard => {
        const isOpen = openCls[standard];
        return (
          <div className="tree-class" key={standard}>
            <div className={`tree-class-row${isOpen ? " active" : ""}`} onClick={() => setOpenCls(prev => ({ ...prev, [standard]: !prev[standard] }))}>
              <span className="tree-emoji">📚</span>
              <span className="tree-label">{standard}</span>
              <span className="tree-count">{groupedClasses[standard].length}</span>
              <span className={`tree-arr${isOpen ? " open" : ""}`}>›</span>
            </div>
            {isOpen && (
              <div className="tree-section">
                {groupedClasses[standard].map(cls => {
                  const isClassSelected        = selectedClass === cls.className;
                  const isAttendanceActive     = isClassSelected && selectedView === "attendance";
                  const isTimetableActive      = isClassSelected && selectedView === "timetable";
                  const isApprovalActive       = isClassSelected && selectedView === "approval";
                  const isParentMsgActive      = isClassSelected && selectedView === "parentMessages";
                  const isApprovalStatusActive = isClassSelected && selectedView === "approvalStatus";
                  const isRegAttActive         = isClassSelected && selectedView === "regularAttendance";
                  const isStuDetailsActive     = isClassSelected && selectedView === "studentDetails";
                  const isMarksheetActive      = isClassSelected && selectedView === "marksheet";

                  return (
                    <div key={cls.className}>
                      <div className={`tree-sec-row${isAttendanceActive ? " active" : ""}`}
                        onClick={() => tap(() => onViewSelect(cls.className, "attendance"))}>
                        <div className="tree-sec-inner">
                          <span className="tree-slabel" style={{ fontWeight:600, color:"#0D47A1" }}>
                            Section {cls.className.split("-")[1]}
                          </span>
                          <span style={{ fontSize:10, color:isAttendanceActive ? "#2196F3" : "#2196F3" }}>
                            📋 Attendance
                          </span>
                        </div>
                      </div>
                      <div className={`tree-tt-row${isTimetableActive ? " active" : ""}`}
                        onClick={() => tap(() => onViewSelect(cls.className, "timetable"))}>
                        <span style={{ fontSize:13 }}>📅</span>
                        <span className="tree-tt-label">Timetable</span>
                      </div>
                      <div className={`tree-approval-row${isApprovalActive ? " active" : ""}`}
                        onClick={() => tap(() => onViewSelect(cls.className, "approval"))}>
                        <span style={{ fontSize:13 }}>🔑</span>
                        <span className="tree-approval-label">LogBook Approvals</span>
                        {pendingCount > 0 && (
                          <span className="tree-badge">{pendingCount}</span>
                        )}
                      </div>
                      <div className={`tree-approval-row${isParentMsgActive ? " active" : ""}`}
                        onClick={() => tap(() => onViewSelect(cls.className, "parentMessages"))}>
                        <span style={{ fontSize:13 }}>📨</span>
                        <span className="tree-approval-label">Parent Messages</span>
                        {unreadCount > 0 && (
                          <span className="tree-badge">{unreadCount}</span>
                        )}
                      </div>
                      <div className={`tree-approval-row${isApprovalStatusActive ? " active" : ""}`}
                        onClick={() => tap(() => onViewSelect(cls.className, "approvalStatus"))}>
                        <span style={{ fontSize:13 }}>📊</span>
                        <span className="tree-approval-label">Approval Status</span>
                      </div>
                      <div className={`tree-tt-row${isRegAttActive ? " active" : ""}`}
                        onClick={() => tap(() => onViewSelect(cls.className, "regularAttendance"))}>
                        <span style={{ fontSize:13 }}>📈</span>
                        <span className="tree-tt-label">Regular Attendance</span>
                      </div>
                      <div className={`tree-tt-row${isStuDetailsActive ? " active" : ""}`}
                        onClick={() => tap(() => onViewSelect(cls.className, "studentDetails"))}>
                        <span style={{ fontSize:13 }}>👩‍🎓</span>
                        <span className="tree-tt-label">Student Details</span>
                      </div>
                      <div className={`tree-tt-row${isMarksheetActive ? " active" : ""}`}
                        onClick={() => tap(() => onViewSelect(cls.className, "marksheet"))}>
                        <span style={{ fontSize:13 }}>📝</span>
                        <span className="tree-tt-label">Marksheet</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  MAIN COMPONENT
// ══════════════════════════════════════════════════════════════════════════════
export default function StudentProfileApp() {
  const API_BASE = process.env.REACT_APP_API_URL || "https://schoolcrm-fzcv.onrender.com";

  const [searchQuery,    setSearchQuery]    = useState("");
  const [unreadCount,    setUnreadCount]    = useState(0);
  const [selectedClass,  setSelectedClass]  = useState(null);
  const [selectedView,   setSelectedView]   = useState("welcome");
  const [students,       setStudents]       = useState([]);
  const [staffClasses,   setStaffClasses]   = useState([]);
  const [attendanceDate, setAttendanceDate] = useState(new Date().toISOString().slice(0, 10));
  const [staffName,      setStaffName]      = useState("");
  const [staffId,        setStaffId]        = useState(null);
  const [staffData,      setStaffData]      = useState(null);
  const [loading,        setLoading]        = useState(true);
  const [saving,         setSaving]         = useState(false);
  const [saveMessage,    setSaveMessage]    = useState("");
  const [stats,          setStats]          = useState({ total:0, present:0, absent:0, late:0 });
  const [pendingCount,   setPendingCount]   = useState(0);
  const [drawerOpen,     setDrawerOpen]     = useState(false);
  const navigate = useNavigate();

  useMemo(() => {
    if (document.getElementById("sp-style")) return;
    const s = document.createElement("style");
    s.id = "sp-style"; s.textContent = CSS;
    document.head.appendChild(s);
  }, []);

  useEffect(() => {
    const token    = localStorage.getItem("token");
    const username = localStorage.getItem("userId");
    const role     = localStorage.getItem("role");
    if (!token || role !== "staff") { navigate("/login"); return; }
    if (username && username !== "undefined") fetchStaffData(username, token);
    else setLoading(false);
  }, [navigate]);

  const fetchStaffData = async (username, token) => {
    try {
      const staffRes = await fetch(`${API_BASE}/api/staff/username/${username}`);
      if (!staffRes.ok) throw new Error("Failed to fetch staff data");
      const staff = await staffRes.json();
      setStaffName(staff.name); setStaffId(staff._id); setStaffData(staff);
      const classesRes = await fetch(`${API_BASE}/api/class-advisors/staff/${staff._id}/classes`);
      if (classesRes.ok) {
        const advisorClasses = await classesRes.json();
        setStaffClasses(advisorClasses);
      }
    } catch (error) { console.error("Error fetching staff data:", error); }
    finally { setLoading(false); }
  };

  const [staffCirculars,   setStaffCirculars]   = useState([]);
  const [readCircularIds,  setReadCircularIds]  = useState(() => new Set());

  useEffect(() => {
    if (!staffId) return;
    try {
      const raw = localStorage.getItem(`readCircularIds_${staffId}`);
      setReadCircularIds(new Set(raw ? JSON.parse(raw) : []));
    } catch { setReadCircularIds(new Set()); }
  }, [staffId]);

  useEffect(() => {
    if (!staffId) return;
    const fetchCirculars = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/circulars`);
        const j   = await res.json();
        if (!j.success) return;
        const list = (j.circulars || []).filter(
          c => !c.isDraft && c.recipientsMeta?.allStaff === true
        );
        setStaffCirculars(list);
      } catch (err) { console.error("Failed to load circulars", err); }
    };
    fetchCirculars();
    const t = setInterval(fetchCirculars, 15000);
    return () => clearInterval(t);
  }, [staffId, API_BASE]);

  const unreadCircularsCount = staffCirculars.filter(c => !readCircularIds.has(c._id)).length;

  const markCircularRead = (id) => {
    setReadCircularIds(prev => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      if (staffId) {
        try { localStorage.setItem(`readCircularIds_${staffId}`, JSON.stringify([...next])); }
        catch (err) { console.error("Failed to save read circular id", err); }
      }
      return next;
    });
  };

  const fetchStudentsByClass = async (className) => {
    try {
      setLoading(true);
      const studentsRes = await fetch(`${API_BASE}/api/admissions/by-class/${encodeURIComponent(className)}`);
      if (!studentsRes.ok) throw new Error("Failed to fetch students");
      let studentList = await studentsRes.json();
      if (!studentList || studentList.length === 0) {
        setStudents([]); setStats({ total:0, present:0, absent:0, late:0 }); return;
      }
      studentList = studentList
        .sort((a, b) => a.studentName.localeCompare(b.studentName))
        .map((s, i) => ({ ...s, rollNo: s.rollNo || String(i + 1) }));
      const attRes = await fetch(`${API_BASE}/api/attendance/${encodeURIComponent(className)}/${attendanceDate}`);
      let attMap = {};
      if (attRes.ok) {
        const attData = await attRes.json();
        attMap = attData.reduce((m, a) => { m[a.studentId] = { status:a.status, remark:a.remark }; return m; }, {});
      }
      const withAtt = studentList.map(s => ({
        ...s,
        attendanceStatus: attMap[s._id]?.status || "P",
        remark: attMap[s._id]?.remark || ""
      }));
      setStudents(withAtt);
      setStats({
        total:   withAtt.length,
        present: withAtt.filter(s => s.attendanceStatus === "P").length,
        absent:  withAtt.filter(s => s.attendanceStatus === "A").length,
        late:    withAtt.filter(s => s.attendanceStatus === "L").length,
      });
    } catch (error) { console.error("Error:", error); setStudents([]); }
    finally { setLoading(false); }
  };

  const handleViewSelect = async (className, view) => {
    setSelectedClass(className); setSelectedView(view); setSearchQuery("");
    if (view === "attendance" && className) await fetchStudentsByClass(className);
  };

  const handleAttendanceChange = (studentId, status, remark) => {
    const updated = students.map(s =>
      s._id === studentId
        ? { ...s, attendanceStatus:status, remark: remark !== undefined ? remark : s.remark }
        : s
    );
    setStudents(updated);
    setStats({
      total:   updated.length,
      present: updated.filter(s => s.attendanceStatus === "P").length,
      absent:  updated.filter(s => s.attendanceStatus === "A").length,
      late:    updated.filter(s => s.attendanceStatus === "L").length,
    });
  };

  const handleMarkAll = (status) => {
    const updated = students.map(s => ({ ...s, attendanceStatus: status }));
    setStudents(updated);
    setStats({
      total:   updated.length,
      present: status === "P" ? updated.length : 0,
      absent:  status === "A" ? updated.length : 0,
      late:    status === "L" ? updated.length : 0,
    });
  };

  const saveAttendance = async () => {
    if (!selectedClass || students.length === 0) {
      setSaveMessage("❌ No students to save");
      setTimeout(() => setSaveMessage(""), 3000); return;
    }
    setSaving(true);
    try {
      const attendanceData = students.map(s => ({
        studentId:   s._id,
        studentName: s.studentName,
        className:   selectedClass,
        rollNo:      s.rollNo,
        date:        attendanceDate,
        status:      s.attendanceStatus,
        remark:      s.remark || ""
      }));
      const response = await fetch(`${API_BASE}/api/attendance/bulk`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ attendance:attendanceData, className:selectedClass, date:attendanceDate, staffId, staffName })
      });
      if (response.ok) { setSaveMessage("✅ Attendance saved!"); setTimeout(() => setSaveMessage(""), 3000); }
      else throw new Error("Failed to save");
    } catch { setSaveMessage("❌ Failed to save"); setTimeout(() => setSaveMessage(""), 3000); }
    finally { setSaving(false); }
  };

  const handleDateChange = async (e) => {
    const newDate = e.target.value;
    setAttendanceDate(newDate);
    if (selectedClass && selectedView === "attendance") await fetchStudentsByClass(selectedClass);
  };

  const handleLogout = () => {
    // Clear session/auth data only — do NOT wipe "schoolName" (or other saved
    // app settings) via localStorage.clear(), or the school name silently
    // reverts to the default the next time it's read.
    Object.keys(localStorage)
      .filter(key => key !== "schoolName")
      .forEach(key => localStorage.removeItem(key));
    navigate("/login");
  };

  const filteredStudents = students.filter(s =>
    s.studentName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    String(s.rollNo || "").includes(searchQuery)
  );

  const parsedClassInfo = useMemo(() => {
    if (!selectedClass) return { className:null, section:null, group:null };
    const dashIdx = selectedClass.lastIndexOf("-");
    const section  = selectedClass.slice(dashIdx + 1);
    const before   = selectedClass.slice(0, dashIdx);
    const parts    = before.split(" ");
    if (parts[1] === "11" || parts[1] === "12")
      return { className:`Class ${parts[1]}`, section, group:parts.slice(2).join(" ") || null };
    return { className:before, section, group:null };
  }, [selectedClass]);

  const sidebarProps = {
    staffClasses,
    onSelect:             cls => handleViewSelect(cls, "attendance"),
    selectedClass,
    selectedView,
    onViewSelect:         handleViewSelect,
    loading:              loading && staffClasses.length === 0 && !staffData,
    advisorStaffId:       staffId,
    pendingCount,
    onPendingCountChange: setPendingCount,
    navigate,
    onItemClick:          () => setDrawerOpen(false),
    unreadCircularsCount,
    unreadCount,
  };

  if (loading && staffClasses.length === 0 && !staffData) {
    return (
      <div style={{ display:"flex", justifyContent:"center", alignItems:"center", height:"100vh", fontFamily:"'Inter',sans-serif", color:"#0D47A1" }}>
        Loading...
      </div>
    );
  }

  return (
    <div className="sp-root">
      {/* Decorative Ornaments */}
      <div className="sp-bg-ornament sp-bg-ornament-1" />
      <div className="sp-bg-ornament sp-bg-ornament-2" />
      <div className="sp-bg-ornament sp-bg-ornament-3" />

      {/* Header */}
      <div className="sp-header">
        <button className="sp-hamburger" onClick={() => setDrawerOpen(true)} aria-label="Open menu">☰</button>
        <div className="sp-header-brand">
          <div className="sp-logo">🎓 Class<span>Staff</span></div>
          <div className="sp-sub">Class Advisor Dashboard — {staffName}</div>
        </div>
        <div className="sp-header-actions">
          {selectedView === "attendance" && (
            <div className="sp-search">
              <span>🔍</span>
              <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search..." />
              {searchQuery && <span onClick={() => setSearchQuery("")} style={{ cursor:"pointer", color:"#90CAF9" }}>✕</span>}
            </div>
          )}
          {pendingCount > 0 && (
            <div onClick={() => { if (staffClasses.length > 0) handleViewSelect(staffClasses[0].className, "approval"); }}
              style={{ position:"relative", cursor:"pointer", background:"rgba(255,255,255,0.1)", border:"1px solid rgba(255,255,255,0.15)", borderRadius:40, padding:"5px 12px", display:"flex", alignItems:"center", gap:5 }}>
              <span style={{ fontSize:15 }}>🔑</span>
              <span style={{ fontSize:11, color:"#90CAF9", fontWeight:600 }}>{pendingCount}</span>
              <span style={{ position:"absolute", top:-3, right:-3, width:8, height:8, borderRadius:"50%", background:"#2196F3", border:"2px solid #0D47A1" }} />
            </div>
          )}
          <BellIcon unreadCount={unreadCount} onClick={() => { if (staffClasses.length > 0) handleViewSelect(staffClasses[0].className, "parentMessages"); }} />
          <button onClick={handleLogout} style={{ background:"rgba(255,255,255,0.1)", border:"1px solid rgba(255,255,255,0.15)", color:"#fff", padding:"7px 16px", borderRadius:40, cursor:"pointer", fontFamily:"'Inter',sans-serif", fontSize:13, whiteSpace:"nowrap" }}>
            Logout
          </button>
        </div>
      </div>

      {/* Drawer Overlay */}
      <div className={`sp-drawer-overlay${drawerOpen ? " open" : ""}`} onClick={() => setDrawerOpen(false)} />

      {/* Mobile Drawer */}
      <div className={`sp-drawer${drawerOpen ? " open" : ""}`}>
        <div className="sp-drawer-header">
          <div className="sp-drawer-title">🎓 Class<span>Staff</span></div>
          <button className="sp-drawer-close" onClick={() => setDrawerOpen(false)}>✕</button>
        </div>
        <div className="sp-drawer-tree">
          <SidebarTree {...sidebarProps} />
        </div>
      </div>

      <div className="sp-body">
        {/* Desktop Sidebar */}
        <div className="sp-sidebar">
          <SidebarTree {...sidebarProps} />
        </div>

        {/* Main Panel */}
        <div className="sp-main">
          <div className="sp-main-body">

            {selectedView === "welcome" && (
              <div className="welcome-box">
                <div className="welcome-icon">🎓</div>
                <div className="welcome-title">Student Attendance Management</div>
                <div className="welcome-sub">
                  Select a class from the {window.innerWidth <= 768 ? "menu (☰)" : "left panel"} to mark attendance,
                  view timetable, manage LogBook approvals, check Approval Status,
                  view Regular Attendance analytics, view Student Details, or manage Marksheets.
                </div>
              </div>
            )}

            {selectedView === "profile" && (
              <StaffProfilePanel staffData={staffData} advisorClasses={staffClasses} />
            )}

            {selectedView === "circulars" && (
              <CircularsPanel
                API_BASE={API_BASE}
                onClose={() => setSelectedView("welcome")}
                readCircularIds={readCircularIds}
                onMarkRead={markCircularRead}
              />
            )}

            {selectedClass && selectedView === "timetable" && (
              <TimetableView className={selectedClass} />
            )}

            {selectedClass && selectedView === "approval" && (
              <ApprovalPanel
                advisorStaffId={staffId}
                onApprovalHandled={() => {
                  if (staffId) {
                    fetch(`${API_BASE}/api/logbook-approvals/pending/${staffId}`)
                      .then(r => r.json())
                      .then(data => setPendingCount(Array.isArray(data) ? data.length : 0))
                      .catch(() => {});
                  }
                }}
              />
            )}

            {selectedClass && selectedView === "parentMessages" && (
              <ParentMessagesPanel
                advisorStaffId={staffId}
                onUnreadCountChange={setUnreadCount}
              />
            )}

            {selectedClass && selectedView === "approvalStatus" && (
              <ApprovalStatusPanel advisorStaffId={staffId} className={selectedClass} API_BASE={API_BASE} />
            )}

            {selectedClass && selectedView === "regularAttendance" && (
              <div style={{ background:"#FFFFFF", borderRadius:20, padding:"20px", border:"1px solid #90CAF9", boxShadow:"0 4px 20px rgba(13,71,161,0.04)" }}>
                <RegularAttendanceView
                  className={parsedClassInfo.className}
                  section={parsedClassInfo.section}
                  group={parsedClassInfo.group}
                />
              </div>
            )}

            {selectedClass && selectedView === "studentDetails" && (
              <StudentDetailsPanel
                className={selectedClass}
                API_BASE={API_BASE}
                staffId={staffId}
                staffData={staffData}
              />
            )}

            {selectedClass && selectedView === "marksheet" && (
              <Marksheet
                className={selectedClass}
                API_BASE={API_BASE}
                staffId={staffId}
                staffData={staffData}
                onClose={() => setSelectedView("studentDetails")}
              />
            )}

            {selectedClass && selectedView === "attendance" && (
              <>
                <div className="stats-grid">
                  <div className="stat-card total">
                    <div className="stat-value">{stats.total}</div>
                    <div className="stat-label">Total Students</div>
                  </div>
                  <div className="stat-card present">
                    <div className="stat-value" style={{ color:"#2196F3" }}>{stats.present}</div>
                    <div className="stat-label">Present</div>
                  </div>
                  <div className="stat-card absent">
                    <div className="stat-value" style={{ color:"#90CAF9" }}>{stats.absent}</div>
                    <div className="stat-label">Absent</div>
                  </div>
                  <div className="stat-card late">
                    <div className="stat-value" style={{ color:"#E3F2FD" }}>{stats.late}</div>
                    <div className="stat-label">Late</div>
                  </div>
                </div>

                <div className="att-action-row" style={{ display:"flex", justifyContent:"space-between", marginBottom:20, gap:12, flexWrap:"wrap" }}>
                  <div>
                    <div style={{ fontWeight:700, fontSize:17, fontFamily:"'Playfair Display',serif" }}>{selectedClass}</div>
                    <div style={{ fontSize:12, color:"#2196F3" }}>{students.length} students enrolled</div>
                  </div>
                  <div style={{ display:"flex", gap:10, alignItems:"center", flexWrap:"wrap" }} className="att-btn-group">
                    <input type="date" value={attendanceDate} onChange={handleDateChange}
                      style={{ padding:"8px 14px", border:"1px solid #90CAF9", borderRadius:40, fontFamily:"'Inter',sans-serif", fontSize:13 }} />
                    <div style={{ display:"flex", gap:6 }}>
                      <button onClick={() => handleMarkAll("P")} style={{ padding:"7px 16px", background:"#E3F2FD", color:"#0D47A1", border:"none", borderRadius:40, cursor:"pointer", fontFamily:"'Inter',sans-serif", fontWeight:600 }}>All P</button>
                      <button onClick={() => handleMarkAll("A")} style={{ padding:"7px 16px", background:"#E3F2FD", color:"#2196F3", border:"none", borderRadius:40, cursor:"pointer", fontFamily:"'Inter',sans-serif", fontWeight:600 }}>All A</button>
                      <button onClick={() => handleMarkAll("L")} style={{ padding:"7px 16px", background:"#E3F2FD", color:"#4A6D8C", border:"none", borderRadius:40, cursor:"pointer", fontFamily:"'Inter',sans-serif", fontWeight:600 }}>All L</button>
                    </div>
                    <button onClick={saveAttendance} disabled={saving}
                      style={{ background:"linear-gradient(90deg,#0D47A1,#2196F3)", color:"#fff", border:"none", padding:"8px 22px", borderRadius:40, cursor:"pointer", fontFamily:"'Inter',sans-serif", fontWeight:600 }}>
                      {saving ? "Saving..." : "💾 Save"}
                    </button>
                  </div>
                </div>

                {saveMessage && (
                  <div style={{ marginBottom:16, padding:10, borderRadius:40, textAlign:"center", background:saveMessage.includes("✅") ? "#E3F2FD" : "#E3F2FD", fontSize:13, fontWeight:600, color:saveMessage.includes("✅") ? "#0D47A1" : "#2196F3" }}>
                    {saveMessage}
                  </div>
                )}

                {loading ? (
                  <div style={{ textAlign:"center", padding:48, color:"#2196F3" }}>Loading students...</div>
                ) : (
                  <div className="student-table-container">
                    <table className="student-table">
                      <thead>
                        <tr>
                          <th>#</th><th>ROLL NO</th><th>STUDENT NAME</th><th>STATUS</th><th>REMARK</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredStudents.map((student, idx) => (
                          <tr key={student._id}>
                            <td>{idx + 1}</td>
                            <td>{student.rollNo}</td>
                            <td>
                              <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                                <div style={{ width:32, height:32, borderRadius:8, background:"#E3F2FD", display:"flex", alignItems:"center", justifyContent:"center", fontWeight:600, color:"#0D47A1", flexShrink:0 }}>
                                  {student.studentName?.charAt(0)}
                                </div>
                                {student.studentName}
                              </div>
                            </td>
                            <td>
                              <div style={{ display:"flex", gap:6 }}>
                                {["P","A","L"].map(status => (
                                  <button key={status} onClick={() => handleAttendanceChange(student._id, status)}
                                    style={{ padding:"5px 12px", borderRadius:40, border:"1px solid #90CAF9", cursor:"pointer", fontFamily:"'Inter',sans-serif", fontWeight:600,
                                      background: student.attendanceStatus === status ? (status === "P" ? "#E3F2FD" : status === "A" ? "#E3F2FD" : "#E3F2FD") : "#fff",
                                      color: student.attendanceStatus === status ? (status === "P" ? "#0D47A1" : status === "A" ? "#2196F3" : "#4A6D8C") : "#2196F3"
                                    }}>
                                    {status}
                                  </button>
                                ))}
                              </div>
                            </td>
                            <td>
                              <input type="text" placeholder="Remark" value={student.remark || ""}
                                onChange={e => handleAttendanceChange(student._id, student.attendanceStatus, e.target.value)}
                                style={{ padding:"6px 12px", border:"1px solid #90CAF9", borderRadius:40, width:110, fontFamily:"'Inter',sans-serif", fontSize:13 }} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {filteredStudents.length === 0 && (
                      <div style={{ padding:48, textAlign:"center", color:"#2196F3" }}>
                        {students.length === 0 ? "No students found in this class" : "No matches for your search"}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

          </div>
        </div>
      </div>
    </div>
  );
}